import { CoverageApiError, type CoverageApi } from '../api/client';
import type {
  AccessManagementPerson,
  AdminCoverageDashboard,
  AdminHouseStreetMatchResult,
  AdminCoverageSection,
  CoverageAnalyticsScope,
  AdminNeighborhoodReview,
  AdminNeighborhoodSummary,
  AdminPublishResult,
  NeighborhoodRecheckResult,
  PlanningCenterSyncStatus,
  StoredIdentitySession,
  PersonSearchResult,
  TeamMember,
  GroupSearchResult,
  ReviewHouse,
  SeasonState,
  VolunteerBootstrap
} from '../domain/types';
import { mountAdminReviewMap, type AdminReviewMapHandle } from './review-map';
import {
  coverageMetricsForScope,
  mountAdminCoverageMap,
  mountSectionPickerMap,
  sectionSearchText,
  type AdminCoverageMapHandle,
  type SectionPickerMapHandle
} from './coverage-map';
import { openPersonUnfinishedMapsPrint } from '../analytics/print-report';
import {
  adminLoadingTemplate,
  adminNeighborhoodCoverageTemplate,
  adminNeighborhoodListTemplate,
  adminReviewTemplate,
  adminShellTemplate,
  coverageMetricsTemplate,
  publishSuccessTemplate,
  reviewMetricsTemplate
} from './templates';

type WorkflowStep = 'review' | 'reset' | 'build';
type WorkflowStepState = 'waiting' | 'active' | 'done' | 'error' | 'skipped' | 'action';

export function startAdminApp(
  root: HTMLDivElement,
  api: CoverageApi,
  identitySession: StoredIdentitySession,
  onSignOut: () => void
): void {
  const adminKey = identitySession.sessionToken;
  let neighborhoods: AdminNeighborhoodSummary[] = [];
  let review: AdminNeighborhoodReview | null = null;
  let pcoStatus: PlanningCenterSyncStatus | null = null;
  let masterCoverage: AdminCoverageDashboard | null = null;
  let neighborhoodCoverage: AdminCoverageDashboard | null = null;
  let bootstrap: VolunteerBootstrap | null = null;
  let season: SeasonState | null = null;
  let reviewMap: AdminReviewMapHandle | null = null;
  let coverageMap: AdminCoverageMapHandle | null = null;
  let assignmentPickerMap: SectionPickerMapHandle | null = null;
  let workflowBusy = false;
  let coverageRefreshTimer: number | undefined;

  void loadNeighborhoods();

  function disposeMaps(): void {
    if (coverageRefreshTimer !== undefined) window.clearInterval(coverageRefreshTimer);
    coverageRefreshTimer = undefined;
    reviewMap?.destroy();
    reviewMap = null;
    coverageMap?.destroy();
    coverageMap = null;
    assignmentPickerMap?.destroy();
    assignmentPickerMap = null;
  }


  async function loadNeighborhoods(): Promise<void> {
    disposeMaps();
    root.innerHTML = adminShellTemplate(adminLoadingTemplate('Loading neighborhoods…'));
    wireLogout();
    try {
      [neighborhoods, pcoStatus, bootstrap, masterCoverage, season] = await Promise.all([
        api.getAdminNeighborhoods(adminKey),
        api.getPlanningCenterStatus(adminKey),
        api.getVolunteerBootstrap(),
        api.getAdminCoverage(adminKey),
        api.getSeason(adminKey)
      ]);
      showNeighborhoods();
    } catch (error) {
      if (error instanceof CoverageApiError && error.statusCode === 401) {
        onSignOut();
        return;
      }
      const message = error instanceof Error ? error.message : 'The admin area could not be loaded.';
      root.innerHTML = adminShellTemplate(
        `<section class=\"center-panel error-panel\"><h1>Admin could not open</h1><p>${escapeAdminHtml(message)}</p><button id=\"admin-load-retry\" class=\"primary-button\" type=\"button\">Try again</button></section>`
      );
      wireLogout();
      root.querySelector<HTMLButtonElement>('#admin-load-retry')?.addEventListener('click', () => void loadNeighborhoods());
    }
  }

  function showNeighborhoods(): void {
    disposeMaps();
    root.innerHTML = adminShellTemplate(adminNeighborhoodListTemplate(neighborhoods, pcoStatus, bootstrap, masterCoverage, season));
    wireLogout();
    wireSimpleAdminNavigation();
    wireAssignedMaps();
    wireAdminMapWorkspace();
    wireNeighborhoodPlanningGroups();
    wireAccessManagement();
    wireSelfRegistrationReview();
    wireNeighborhoodRecheck();
    wireSeasonManagement();
    wireAssignmentActivityReset();
    document.querySelector<HTMLButtonElement>('#sync-planning-center')?.addEventListener('click', () => {
      void syncPlanningCenter(false);
    });
    document.querySelectorAll<HTMLButtonElement>('[data-neighborhood-id]').forEach((button) => {
      button.addEventListener('click', () => {
        const id = button.dataset.neighborhoodId;
        if (!id) return;
        const pendingChanges = Number(button.dataset.pendingChanges ?? 0);
        if (pendingChanges > 0) void loadReview(id);
        else if (button.dataset.neighborhoodPublished === 'true') void loadCoverage(id);
        else void loadReview(id);
      });
    });
    void maybeAutoSyncPlanningCenter();
  }

  function wireSimpleAdminNavigation(): void {
    const buttons = Array.from(document.querySelectorAll<HTMLButtonElement>('[data-simple-admin-view]'));
    const panels = Array.from(document.querySelectorAll<HTMLElement>('[data-simple-admin-panel]'));
    if (!buttons.length || !panels.length) return;

    type SimpleAdminView = 'ASSIGN' | 'ASSIGNED' | 'NEIGHBORHOODS' | 'TOOLS';
    const isView = (value: string | null): value is SimpleAdminView =>
      value === 'ASSIGN' || value === 'ASSIGNED' || value === 'NEIGHBORHOODS' || value === 'TOOLS';
    const stored = window.sessionStorage.getItem('buckeyeCoverageAdminView');
    let activeView: SimpleAdminView = isView(stored) ? stored : 'ASSIGN';

    const applyView = (nextView: SimpleAdminView): void => {
      activeView = nextView;
      for (const button of buttons) {
        const active = button.dataset.simpleAdminView === activeView;
        button.classList.toggle('active', active);
        button.setAttribute('aria-pressed', String(active));
      }
      for (const panel of panels) {
        const active = panel.dataset.simpleAdminPanel === activeView;
        panel.classList.toggle('hidden', !active);
        panel.setAttribute('aria-hidden', String(!active));
      }
      window.sessionStorage.setItem('buckeyeCoverageAdminView', activeView);
      if (activeView === 'ASSIGN') {
        window.setTimeout(() => window.dispatchEvent(new Event('resize')), 0);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    for (const button of buttons) {
      button.addEventListener('click', () => {
        const next = button.dataset.simpleAdminView ?? '';
        if (isView(next)) applyView(next);
      });
    }
    applyView(activeView);
  }

  function wireAssignedMaps(): void {
    const searchInput = document.querySelector<HTMLInputElement>('#admin-assigned-map-search');
    const clearSearch = document.querySelector<HTMLButtonElement>('#clear-admin-assigned-map-search');
    const cards = Array.from(document.querySelectorAll<HTMLElement>('[data-assignment-card]'));
    const count = document.querySelector<HTMLElement>('#admin-assigned-map-count');
    const message = document.querySelector<HTMLElement>('#admin-assigned-map-message');
    const filterButtons = Array.from(document.querySelectorAll<HTMLButtonElement>('[data-assigned-kind]'));
    const empty = document.querySelector<HTMLElement>('#admin-assigned-empty');
    const storedKind = window.sessionStorage.getItem('buckeyeCoverageAssignedKind');
    let activeKind = storedKind === 'PERSON' || storedKind === 'GROUP' || storedKind === 'UNASSIGNED' ? storedKind : 'ALL';

    const applySearch = (): void => {
      const query = searchInput?.value.trim().toLocaleLowerCase() ?? '';
      let shown = 0;
      for (const card of cards) {
        const visible = (activeKind === 'ALL' || card.dataset.assignmentKind === activeKind)
          && (!query || (card.dataset.assignmentSearch ?? '').includes(query));
        card.hidden = !visible;
        if (visible) shown += 1;
      }
      const label = activeKind === 'GROUP' ? 'group' : activeKind === 'PERSON' ? 'person assignment' : activeKind === 'UNASSIGNED' ? 'unassigned neighborhood' : 'assignment or neighborhood';
      if (count) count.textContent = `${shown.toLocaleString()} ${query ? 'matching ' : ''}${label}${shown === 1 ? '' : 's'} with sections`;
      if (empty) empty.hidden = shown > 0;
      for (const button of filterButtons) button.setAttribute('aria-pressed', String(button.dataset.assignedKind === activeKind));
    };
    for (const button of filterButtons) button.addEventListener('click', () => {
      activeKind = button.dataset.assignedKind ?? 'ALL';
      window.sessionStorage.setItem('buckeyeCoverageAssignedKind', activeKind);
      applySearch();
    });
    searchInput?.addEventListener('input', applySearch);
    clearSearch?.addEventListener('click', () => {
      if (searchInput) searchInput.value = '';
      applySearch();
      searchInput?.focus();
    });
    applySearch();

    const setMessage = (text: string, error = false): void => {
      if (!message) return;
      message.textContent = text;
      message.classList.toggle('hidden', !text);
      message.classList.toggle('error', error);
      message.classList.toggle('success', Boolean(text) && !error);
    };

    const refreshAfterRelease = async (successText: string): Promise<void> => {
      [neighborhoods, bootstrap, masterCoverage] = await Promise.all([
        api.getAdminNeighborhoods(adminKey),
        api.getVolunteerBootstrap(),
        api.getAdminCoverage(adminKey)
      ]);
      showNeighborhoods();
      const refreshed = document.querySelector<HTMLElement>('#admin-assigned-map-message');
      if (refreshed) {
        refreshed.textContent = successText;
        refreshed.classList.remove('hidden', 'error');
        refreshed.classList.add('success');
      }
    };

    // One audited Admin API path handles a person's assignment, a group reservation,
    // or an unassigned published section. Choose the credited participants before updating.
    // Every section in a multi-section batch receives the same roster; an empty roster
    // intentionally allocates no personal credit, even if a section has an assignee.
    let completionBusy = false;
    const selectCompletionParticipants = (sectionCount: number, doors: number): Promise<TeamMember[] | null> =>
      new Promise((resolve) => {
        const dialog = document.createElement('dialog');
        dialog.className = 'admin-credit-dialog';
        dialog.setAttribute('aria-label', 'Record completion credit');
        dialog.innerHTML = `<div class="admin-credit-modal">
          <p class="eyebrow">Admin completion</p>
          <h2>Complete ${sectionCount.toLocaleString()} section${sectionCount === 1 ? '' : 's'}</h2>
          <p>${doors.toLocaleString()} total doors. Choose people to receive equal credit across these sections, or finish without assigning personal credit.</p>
          <label for="admin-credit-lookup">Who participated? <span>(optional)</span></label>
          <div class="admin-credit-entry"><input id="admin-credit-lookup" type="search" autocomplete="off" placeholder="Search people or enter a name" /><button type="button" class="secondary-button compact-button" id="admin-credit-add-name">Add name</button></div>
          <div id="admin-credit-search-results" class="admin-credit-results" aria-live="polite"></div>
          <div id="admin-credit-chips" class="admin-credit-chips" aria-live="polite"></div>
          <p id="admin-credit-explanation" class="admin-credit-explanation">No individual credit will be recorded unless you add names.</p>
          <div class="admin-credit-buttons"><button type="button" class="secondary-button compact-button" id="admin-credit-cancel">Cancel</button><button type="button" class="secondary-button compact-button" id="admin-credit-none">Complete without names</button><button type="button" class="primary-button compact-button" id="admin-credit-submit" disabled>Complete and credit (0)</button></div>
        </div>`;
        document.body.append(dialog);
        const input = dialog.querySelector<HTMLInputElement>('#admin-credit-lookup')!;
        const addButton = dialog.querySelector<HTMLButtonElement>('#admin-credit-add-name')!;
        const results = dialog.querySelector<HTMLElement>('#admin-credit-search-results')!;
        const chips = dialog.querySelector<HTMLElement>('#admin-credit-chips')!;
        const explain = dialog.querySelector<HTMLElement>('#admin-credit-explanation')!;
        const submit = dialog.querySelector<HTMLButtonElement>('#admin-credit-submit')!;
        const participants = new Map<string, TeamMember>();
        let searchSequence = 0;
        let searchTimer: number | undefined;
        let finished = false;
        const finish = (members: TeamMember[] | null): void => {
          if (finished) return;
          finished = true;
          if (searchTimer !== undefined) window.clearTimeout(searchTimer);
          dialog.close();
          dialog.remove();
          resolve(members);
        };
        dialog.addEventListener('cancel', (event) => { event.preventDefault(); finish(null); });
        dialog.querySelector<HTMLButtonElement>('#admin-credit-cancel')!.addEventListener('click', () => finish(null));
        dialog.querySelector<HTMLButtonElement>('#admin-credit-none')!.addEventListener('click', () => finish([]));
        submit.addEventListener('click', () => finish([...participants.values()]));
        const showSelected = (): void => {
          chips.replaceChildren();
          for (const [key, person] of participants) {
            const chip = document.createElement('span');
            chip.className = 'admin-credit-chip';
            const name = document.createElement('span');
            name.textContent = person.name;
            const remove = document.createElement('button');
            remove.type = 'button';
            remove.textContent = '×';
            remove.setAttribute('aria-label', `Remove ${person.name}`);
            remove.addEventListener('click', () => { participants.delete(key); showSelected(); });
            chip.append(name, remove);
            chips.append(chip);
          }
          submit.disabled = !participants.size;
          submit.textContent = `Complete and credit (${participants.size})`;
          explain.textContent = participants.size
            ? `${doors.toLocaleString()} doors total, divided equally between ${participants.size} ${participants.size === 1 ? 'person' : 'people'} (about ${(doors / participants.size).toFixed(1)} doors each). Each section is divided individually.`
            : 'No personal credit will be recorded unless you add names.';
        };
        const addPerson = (person: TeamMember): void => {
          const name = person.name.trim().replace(/\s+/g, ' ');
          if (!name || name.length > 120) return;
          const key = person.personId ? `id:${person.personId}` : `name:${name.toLocaleLowerCase()}`;
          if (!participants.has(key) && participants.size >= 30) {
            explain.textContent = 'You can credit up to 30 people on one completion.';
            return;
          }
          // Prefer a Planning Center identity over a free-text match when both refer to the same person.
          if (person.personId) participants.delete(`name:${name.toLocaleLowerCase()}`);
          if (person.personId || ![...participants.values()].some((candidate) => candidate.name.toLocaleLowerCase() === name.toLocaleLowerCase() && candidate.personId)) {
            participants.set(key, { ...person, name });
          }
          searchSequence += 1;
          if (searchTimer !== undefined) window.clearTimeout(searchTimer);
          input.value = '';
          results.replaceChildren();
          showSelected();
        };
        const addTyped = (): void => {
          // Multiple typed names can be separated with commas or new lines.
          const names = input.value.split(/[,\n]/).map((part) => part.trim()).filter(Boolean);
          for (const name of names) addPerson({ name, source: 'CUSTOM' });
        };
        addButton.addEventListener('click', addTyped);
        input.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') { event.preventDefault(); addTyped(); }
        });
        input.addEventListener('input', () => {
          const query = input.value.trim();
          const sequence = ++searchSequence;
          if (searchTimer !== undefined) window.clearTimeout(searchTimer);
          results.replaceChildren();
          if (query.length < 2 || query.includes(',')) return;
          searchTimer = window.setTimeout(() => {
            void api.searchAdminPeople(adminKey, query).then((people) => {
              if (finished || sequence !== searchSequence) return;
              results.replaceChildren();
              for (const person of people.slice(0, 8)) {
                const row = document.createElement('button');
                row.className = 'admin-credit-result';
                row.type = 'button';
                row.textContent = person.displayName;
                row.addEventListener('click', () => addPerson({ name: person.displayName, source: 'PLANNING_CENTER', personId: person.id }));
                results.append(row);
              }
            }).catch(() => {
              if (sequence === searchSequence && !finished) results.textContent = 'Search unavailable. You can still add a name manually.';
            });
          }, 250);
        });
        dialog.showModal();
        input.focus();
      });
    const completeSelectedSections = async (sectionIds: string[]): Promise<void> => {
      if (completionBusy || !sectionIds.length) return;
      const uniqueIds = [...new Set(sectionIds)];
      const sections = uniqueIds.map((id) => masterCoverage?.sections.find((candidate) => candidate.id === id));
      if (sections.some((section) => !section || section.status === 'COMPLETE')) {
        setMessage('One of those sections is already complete or no longer available. Refresh first.', true);
        return;
      }
      const doorCount = sections.reduce((total, section) => total + (section?.doors ?? 0), 0);
      completionBusy = true;
      const teamMembers = await selectCompletionParticipants(uniqueIds.length, doorCount);
      if (teamMembers === null) { completionBusy = false; return; }
      document.querySelectorAll<HTMLButtonElement>('.admin-complete-one, .admin-complete-all, .admin-complete-selected').forEach((button) => { button.disabled = true; });
      let completed = 0;
      let lastError: string | null = null;
      for (const sectionId of uniqueIds) {
        try { await api.completeAdminSection(adminKey, sectionId, teamMembers); completed += 1; }
        catch (error) { lastError = error instanceof Error ? error.message : 'Could not complete section.'; break; }
      }
      completionBusy = false;
      const who = teamMembers.length ? ` Credit recorded for ${teamMembers.map((person) => person.name).join(', ')}.` : ' No personal credit recorded.';
      const statusText = `${completed.toLocaleString()} of ${uniqueIds.length.toLocaleString()} section${uniqueIds.length === 1 ? '' : 's'} marked complete.${completed ? who : ''}${lastError ? ` Stopped: ${lastError}` : ''}`;
      try { await refreshAfterRelease(statusText); }
      catch (error) { setMessage(error instanceof Error ? error.message : 'The update finished, but the Admin view could not refresh.', true); }
    };
    document.querySelectorAll<HTMLElement>('.admin-assigned-map-card').forEach((card) => {
      const checkboxes = [...card.querySelectorAll<HTMLInputElement>('.admin-complete-check')];
      const selectedButton = card.querySelector<HTMLButtonElement>('.admin-complete-selected');
      const allButton = card.querySelector<HTMLButtonElement>('.admin-complete-all');
      const updateSelected = (): void => {
        const checked = checkboxes.filter((input) => input.checked);
        if (selectedButton) {
          selectedButton.disabled = checked.length === 0 || completionBusy;
          selectedButton.textContent = `Complete selected (${checked.length})`;
        }
      };
      checkboxes.forEach((checkbox) => checkbox.addEventListener('change', updateSelected));
      selectedButton?.addEventListener('click', () => void completeSelectedSections(checkboxes.filter((input) => input.checked).map((input) => input.dataset.sectionId ?? '').filter(Boolean)));
      allButton?.addEventListener('click', () => void completeSelectedSections(checkboxes.map((input) => input.dataset.sectionId ?? '').filter(Boolean)));
    });
    document.querySelectorAll<HTMLButtonElement>('.admin-complete-one').forEach((button) => button.addEventListener('click', () => {
      const id = button.dataset.sectionId;
      if (id) void completeSelectedSections([id]);
    }));
    document.querySelectorAll<HTMLButtonElement>('.admin-reopen-unassigned').forEach((button) => button.addEventListener('click', () => {
      const sectionId = button.dataset.sectionId;
      const section = masterCoverage?.sections.find((candidate) => candidate.id === sectionId);
      if (!sectionId || !section) return;
      if (!window.confirm(`Undo completion for ${section.neighborhoodName} section ${section.sectionNumber}? It will return to ${section.groupId ? 'its existing group reservation' : 'the unassigned pool'}, and completed totals will decrease.`)) return;
      button.disabled = true;
      void api.reopenAdminUnassignedSection(adminKey, sectionId)
        .then(() => refreshAfterRelease(`Reopened ${section.neighborhoodName} section ${section.sectionNumber}.`))
        .catch((error) => {
          setMessage(error instanceof Error ? error.message : 'Could not undo completion.', true);
          button.disabled = false;
        });
    }));

    document.querySelectorAll<HTMLButtonElement>('.admin-release-assignment').forEach((button) => {
      button.addEventListener('click', () => {
        const assignmentId = button.dataset.releaseAssignmentId;
        const personName = button.dataset.releasePersonName ?? 'this person';
        if (!assignmentId || !masterCoverage) return;
        const unfinished = masterCoverage.sections.filter((section) =>
          section.assignmentId === assignmentId && section.status !== 'COMPLETE'
        );
        if (!unfinished.length) {
          setMessage('This assignment no longer has unfinished sections to release.', true);
          return;
        }
        const doors = unfinished.reduce((sum, section) => sum + section.doors, 0);
        if (!window.confirm(`Release ${unfinished.length} unfinished section${unfinished.length === 1 ? '' : 's'} (${doors.toLocaleString()} doors) from ${personName}? Completed sections will stay completed. The released sections will become available to assign again.`)) return;
        button.disabled = true;
        button.textContent = 'Releasing…';
        setMessage('');
        void api.releaseAdminSections(adminKey, assignmentId, unfinished.map((section) => section.id))
          .then((result) => refreshAfterRelease(`Released ${result.releasedRouteIds.length.toLocaleString()} unfinished section${result.releasedRouteIds.length === 1 ? '' : 's'} from ${personName}. Completed work was preserved.`))
          .catch((error) => {
            setMessage(error instanceof Error ? error.message : 'Could not release this assignment.', true);
            button.disabled = false;
            button.textContent = unfinished.length === 1 ? 'Release unfinished section' : `Release all ${unfinished.length} unfinished sections`;
          });
      });
    });

    document.querySelectorAll<HTMLButtonElement>('.admin-release-section').forEach((button) => {
      button.addEventListener('click', () => {
        const assignmentId = button.dataset.releaseAssignmentId;
        const sectionId = button.dataset.releaseSectionId;
        const label = button.dataset.releaseSectionLabel ?? 'this section';
        if (!assignmentId || !sectionId) return;
        if (!window.confirm(`Release ${label}? It will become available to assign again. Any completed sections in this assignment will stay completed.`)) return;
        button.disabled = true;
        button.textContent = 'Releasing…';
        setMessage('');
        void api.releaseAdminSections(adminKey, assignmentId, [sectionId])
          .then((result) => refreshAfterRelease(`Released ${label}. ${result.remainingRouteIds.length.toLocaleString()} section${result.remainingRouteIds.length === 1 ? '' : 's'} remain on that assignment.`))
          .catch((error) => {
            setMessage(error instanceof Error ? error.message : 'Could not release this section.', true);
            button.disabled = false;
            button.textContent = 'Release section';
          });
      });
    });

    document.querySelectorAll<HTMLButtonElement>('.admin-return-group-assignment').forEach((button) => {
      button.addEventListener('click', () => {
        const assignmentId = button.dataset.returnGroupAssignmentId;
        const personName = button.dataset.returnGroupPersonName ?? 'the assigned member';
        if (!assignmentId || !masterCoverage) return;
        const sections = masterCoverage.sections.filter((section) => section.assignmentId === assignmentId);
        if (!sections.length || !sections.some((section) => section.groupId)) {
          setMessage('This assignment is no longer linked to a group. Refresh the map and try again.', true);
          return;
        }
        const doors = sections.reduce((sum, section) => sum + section.doors, 0);
        if (!window.confirm(`Return all ${sections.length.toLocaleString()} sections (${doors.toLocaleString()} doors) from ${personName} to the group? This clears progress and completion credit so the group leader can assign them again. House markers such as Empty lot and No Soliciting are kept.`)) return;
        button.disabled = true;
        button.textContent = 'Returning to group…';
        setMessage('');
        void api.returnAdminAssignmentToGroup(adminKey, assignmentId)
          .then((result) => refreshAfterRelease(`Returned ${result.returnedRouteIds.length.toLocaleString()} sections from ${personName} to ${result.groupName}. Completion credit was removed; the sections are waiting for the group leader.`))
          .catch((error) => {
            setMessage(error instanceof Error ? error.message : 'Could not return this assignment to its group.', true);
            button.disabled = false;
            button.textContent = `Return ${sections.length.toLocaleString()} sections from ${personName} to group · ${doors.toLocaleString()} doors`;
          });
      });
    });

    document.querySelectorAll<HTMLButtonElement>('.admin-reopen-section').forEach((button) => {
      button.addEventListener('click', () => {
        const assignmentId = button.dataset.reopenAssignmentId;
        const sectionId = button.dataset.reopenSectionId;
        const label = button.dataset.reopenSectionLabel ?? 'this section';
        const targetKind = button.dataset.reopenTargetKind === 'GROUP' ? 'GROUP' : 'PERSON';
        const targetLabel = button.dataset.reopenTargetLabel ?? 'original assignee';
        const reopenTargetCopy = targetKind === 'GROUP'
          ? `the ${targetLabel} group assignment`
          : `${targetLabel}'s assignment`;
        const section = masterCoverage?.sections.find((candidate) => candidate.id === sectionId);
        if (!assignmentId || !sectionId || !section) return;
        if (!window.confirm(`Undo completion for ${label} (${section.doors.toLocaleString()} doors)? It will return to ${reopenTargetCopy}, reduce completed door totals, and count as assigned/in progress again.`)) return;
        button.disabled = true;
        button.textContent = 'Reopening…';
        setMessage('');
        void api.reopenAdminSection(adminKey, assignmentId, sectionId)
          .then(() => refreshAfterRelease(`Reopened ${label} (${section.doors.toLocaleString()} doors) and restored it to ${reopenTargetCopy}.`))
          .catch((error) => {
            setMessage(error instanceof Error ? error.message : 'Could not undo section completion.', true);
            button.disabled = false;
            button.textContent = 'Undo complete';
          });
      });
    });
  }

  function wireNeighborhoodPlanningGroups(): void {
    const tabs = Array.from(document.querySelectorAll<HTMLButtonElement>('[data-neighborhood-group-filter]'));
    const cards = Array.from(document.querySelectorAll<HTMLElement>('.neighborhood-card-shell'));
    const status = document.querySelector<HTMLElement>('#neighborhood-group-filter-status');
    const searchInput = document.querySelector<HTMLInputElement>('#coverage-neighborhood-search');
    const clearSearch = document.querySelector<HTMLButtonElement>('#clear-coverage-neighborhood-search');
    let activeFilter: 'ALL' | 'CURRENT' | 'FUTURE' | 'NOT_IN_SCOPE' = 'CURRENT';

    const compactSearch = (value: string): string => value.toLocaleLowerCase().replace(/[^a-z0-9]+/g, '');
    const applyFilter = (): void => {
      const query = searchInput?.value.trim().toLocaleLowerCase() ?? '';
      const compactQuery = compactSearch(query);
      let shown = 0;
      for (const card of cards) {
        const planningGroupMatches = query.length > 0
          || activeFilter === 'ALL'
          || card.dataset.neighborhoodPlanningGroup === activeFilter;
        const searchable = card.dataset.neighborhoodSearch ?? '';
        const searchMatches = !query
          || searchable.includes(query)
          || compactSearch(searchable).includes(compactQuery);
        const visible = planningGroupMatches && searchMatches;
        card.hidden = !visible;
        if (visible) card.style.removeProperty('display');
        else card.style.setProperty('display', 'none', 'important');
        if (visible) shown += 1;
      }
      for (const tab of tabs) {
        const group = tab.dataset.neighborhoodGroupFilter;
        const active = group === activeFilter;
        tab.classList.toggle('active', active);
        tab.setAttribute('aria-pressed', String(active));
        const count = group === 'ALL'
          ? cards.length
          : cards.filter((card) => card.dataset.neighborhoodPlanningGroup === group).length;
        const countElement = tab.querySelector<HTMLElement>('span');
        if (countElement) countElement.textContent = count.toLocaleString();
      }
      if (status) {
        if (query) {
          status.textContent = `Showing ${shown.toLocaleString()} matching neighborhood${shown === 1 ? '' : 's'} across all groups.`;
        } else {
          const label = activeFilter === 'ALL' ? 'all' : activeFilter === 'CURRENT' ? 'current' : activeFilter === 'FUTURE' ? 'future' : 'not in scope';
          status.textContent = `Showing ${shown.toLocaleString()} ${label} neighborhood${shown === 1 ? '' : 's'}.`;
        }
      }
    };

    for (const tab of tabs) {
      tab.addEventListener('click', () => {
        const next = tab.dataset.neighborhoodGroupFilter;
        if (next === 'ALL' || next === 'CURRENT' || next === 'FUTURE' || next === 'NOT_IN_SCOPE') {
          activeFilter = next;
          applyFilter();
        }
      });
    }


    searchInput?.addEventListener('input', applyFilter);
    clearSearch?.addEventListener('click', () => {
      if (searchInput) searchInput.value = '';
      applyFilter();
      searchInput?.focus();
    });

    document.querySelectorAll<HTMLSelectElement>('[data-neighborhood-planning-group-select]').forEach((select) => {
      select.addEventListener('change', () => {
        const neighborhoodId = select.dataset.neighborhoodId;
        const planningGroup = select.value === 'FUTURE'
          ? 'FUTURE' as const
          : select.value === 'NOT_IN_SCOPE'
            ? 'NOT_IN_SCOPE' as const
            : 'CURRENT' as const;
        const shell = select.closest<HTMLElement>('.neighborhood-card-shell');
        const message = shell?.querySelector<HTMLElement>('[data-neighborhood-planning-group-message]');
        if (!neighborhoodId || !shell) return;
        const previous = shell.dataset.neighborhoodPlanningGroup === 'FUTURE'
          ? 'FUTURE'
          : shell.dataset.neighborhoodPlanningGroup === 'NOT_IN_SCOPE'
            ? 'NOT_IN_SCOPE'
            : 'CURRENT';
        select.disabled = true;
        if (message) message.textContent = 'Saving…';
        void api.setNeighborhoodPlanningGroup(adminKey, neighborhoodId, planningGroup).then((updated) => {
          shell.dataset.neighborhoodPlanningGroup = updated.planningGroup;
          const local = neighborhoods.find((neighborhood) => neighborhood.id === neighborhoodId);
          if (local) local.planningGroup = updated.planningGroup;
          if (message) message.textContent = `Saved as ${updated.planningGroup === 'CURRENT' ? 'Current' : updated.planningGroup === 'FUTURE' ? 'Future' : 'Not in scope'}.`;
          applyFilter();
          window.setTimeout(() => { if (message) message.textContent = ''; }, 2200);
        }).catch((error) => {
          select.value = previous;
          if (message) message.textContent = error instanceof Error ? error.message : 'Could not save neighborhood group.';
        }).finally(() => { select.disabled = false; });
      });
    });

    applyFilter();
  }

  function wireAdminMapWorkspace(): void {
    if (!masterCoverage) return;
    let dashboard = masterCoverage;
    const form = document.querySelector<HTMLFormElement>('#admin-assignment-form');
    const targetInput = document.querySelector<HTMLInputElement>('#admin-assignment-target-search');
    const targetResults = document.querySelector<HTMLDivElement>('#admin-assignment-target-results');
    const selectedTargetBox = document.querySelector<HTMLDivElement>('#admin-selected-assignment-target');
    const allowAdditionalRow = document.querySelector<HTMLElement>('#admin-allow-additional-row');
    const summary = document.querySelector<HTMLDivElement>('#admin-section-picker-summary');
    const assignButton = document.querySelector<HTMLButtonElement>('#admin-assign-button');
    const clearSelectedButton = document.querySelector<HTMLButtonElement>('#admin-clear-selected-sections');
    const assignModeButton = document.querySelector<HTMLButtonElement>('#admin-map-mode-assign');
    const analyticsModeButton = document.querySelector<HTMLButtonElement>('#admin-map-mode-analytics');
    const assignmentControls = document.querySelector<HTMLElement>('#admin-map-assignment-controls');
    const assignmentFinal = document.querySelector<HTMLElement>('#admin-map-assignment-final');
    const analyticsControls = document.querySelector<HTMLElement>('#admin-map-analytics-controls');
    const modeDescription = document.querySelector<HTMLElement>('#admin-map-mode-description');
    const mapSearch = document.querySelector<HTMLInputElement>('#master-coverage-search');
    const mapCount = document.querySelector<HTMLSpanElement>('#master-coverage-search-count');
    const mapSearchRow = document.querySelector<HTMLElement>('#admin-shared-map-search-row');
    const assignmentSearchSlot = document.querySelector<HTMLElement>('#admin-map-search-assignment-slot');
    const analyticsSearchSlot = document.querySelector<HTMLElement>('#admin-map-search-analytics-slot');
    const selectedLegend = document.querySelector<HTMLElement>('#admin-selected-section-legend');
    const completedLegend = document.querySelector<HTMLElement>('#admin-completed-section-legend');
    const scopeInput = document.querySelector<HTMLInputElement>('#master-coverage-search-scope');
    const scopeResults = document.querySelector<HTMLDivElement>('#master-coverage-search-scope-results');
    const scopeCurrent = document.querySelector<HTMLDivElement>('#master-coverage-search-scope-current');
    const scopeMetrics = document.querySelector<HTMLDivElement>('#master-coverage-search-metrics');
    const personUnstartedMaps = document.querySelector<HTMLDivElement>('#admin-person-unstarted-maps');
    const scopeClear = document.querySelector<HTMLButtonElement>('#master-coverage-search-scope-clear');
    const houseCorrectionNeighborhood = document.querySelector<HTMLSelectElement>('#admin-house-correction-neighborhood');
    const houseCorrectionSearch = document.querySelector<HTMLInputElement>('#admin-house-correction-search');
    const houseCorrectionStatus = document.querySelector<HTMLParagraphElement>('#admin-house-correction-status');
    const houseCorrectionResults = document.querySelector<HTMLDivElement>('#admin-house-correction-results');

    type UnifiedTarget =
      | { type: 'PERSON'; id: string; label: string; person: PersonSearchResult }
      | { type: 'GROUP'; id: string; label: string; group: GroupSearchResult };
    type MapMode = 'ASSIGN' | 'ANALYTICS';

    const scopes = adminAnalyticsScopes(dashboard);
    const overallScope = scopes.find((scope) => scope.type === 'OVERALL') ?? {
      type: 'OVERALL' as const,
      id: 'OVERALL' as const,
      label: 'Whole church'
    };
    let currentMode: MapMode = 'ASSIGN';
    let currentScope: CoverageAnalyticsScope = overallScope;
    let selectedTarget: UnifiedTarget | null = null;
    let selectedSections: AdminCoverageSection[] = [];
    let searchTimer = 0;
    let searchSequence = 0;
    let houseCorrectionReview: AdminNeighborhoodReview | null = null;
    let houseCorrectionLoadSequence = 0;
    let houseCorrectionNotice = '';
    const savingHouseConditions = new Set<string>();

    const setMessage = (text: string, error = false): void => {
      const message = document.querySelector<HTMLDivElement>('#admin-assignment-message');
      if (!message) return;
      message.textContent = text;
      message.classList.toggle('hidden', !text);
      message.classList.toggle('error', error);
      message.classList.toggle('success', Boolean(text) && !error);
    };
    const houseConditionLabel = (condition: ReviewHouse['conditionStatus']): string => {
      const labels: Record<string, string> = { NO_SOLICITING: 'No Soliciting', EMPTY_LOT: 'Empty lot', UNDER_CONSTRUCTION: 'Under construction' };
      return condition ? labels[condition] ?? 'No condition' : 'No condition';
    };
    const renderHouseCorrections = (): void => {
      if (!houseCorrectionResults || !houseCorrectionStatus) return;
      if (!houseCorrectionReview) {
        houseCorrectionResults.innerHTML = '';
        houseCorrectionStatus.textContent = houseCorrectionNotice || 'Choose a neighborhood to review its houses, including No Soliciting markers.';
        return;
      }
      const query = houseCorrectionSearch?.value.trim().toLocaleLowerCase() ?? '';
      const allMatches = houseCorrectionReview.houses.filter((house) => `${house.address} ${house.street} ${house.id}`.toLocaleLowerCase().includes(query));
      const matches = (query ? allMatches : houseCorrectionReview.houses.filter((house) => Boolean(house.conditionStatus)))
        .sort((left, right) => left.address.localeCompare(right.address)).slice(0, 20);
      const markedCount = houseCorrectionReview.houses.filter((house) => Boolean(house.conditionStatus)).length;
      houseCorrectionStatus.textContent = houseCorrectionNotice || (query
        ? `${matches.length.toLocaleString()} matching house${matches.length === 1 ? '' : 's'}${allMatches.length > 20 ? ' (showing first 20)' : ''}.`
        : `${markedCount.toLocaleString()} house${markedCount === 1 ? '' : 's'} already marked. Search by house number or street to add or change a marker.`);
      if (!matches.length) {
        houseCorrectionResults.innerHTML = query
          ? '<div class="person-search-state">No matching house. Check the house number or street.</div>'
          : '<div class="person-search-state">No houses are marked yet. Search by house number or street to add a correction.</div>';
        return;
      }
      const options = [['NO_SOLICITING', 'No Soliciting'], ['EMPTY_LOT', 'Empty lot'], ['UNDER_CONSTRUCTION', 'Under construction']] as const;
      houseCorrectionResults.innerHTML = matches.map((house) => {
        const disabled = savingHouseConditions.has(house.id) ? 'disabled' : '';
        const actions = options.map(([value, label]) => `<button type="button" class="secondary-button compact-button" data-admin-house-condition="${value}" data-admin-house-id="${escapeAdminHtml(house.id)}" ${disabled}>${label}</button>`).join('');
        const clear = house.conditionStatus ? `<button type="button" class="text-button" data-admin-house-condition="CLEAR" data-admin-house-id="${escapeAdminHtml(house.id)}" ${disabled}>Clear marker</button>` : '';
        return `<article class="coverage-scope-current admin-house-correction-row"><div><strong>${escapeAdminHtml(house.address || house.id)}</strong><span>${escapeAdminHtml(house.street)} · Current: ${houseConditionLabel(house.conditionStatus)}</span></div><div class="admin-house-correction-actions">${actions}${clear}</div></article>`;
      }).join('');
      houseCorrectionResults.querySelectorAll<HTMLButtonElement>('[data-admin-house-condition]').forEach((button) => {
        button.addEventListener('click', () => {
          const houseId = button.dataset.adminHouseId;
          const requested = button.dataset.adminHouseCondition;
          const conditionStatus = requested === 'CLEAR' ? null
            : requested === 'NO_SOLICITING' || requested === 'EMPTY_LOT' || requested === 'UNDER_CONSTRUCTION' ? requested
              : undefined;
          const neighborhoodId = houseCorrectionReview?.neighborhood.id;
          if (!houseId || !neighborhoodId || conditionStatus === undefined || savingHouseConditions.has(houseId)) return;
          savingHouseConditions.add(houseId);
          houseCorrectionNotice = `Saving ${houseCorrectionReview?.houses.find((house) => house.id === houseId)?.address || 'house'}…`;
          renderHouseCorrections();
          void api.setAdminHouseCondition(adminKey, neighborhoodId, houseId, conditionStatus)
            .then((updatedHouse) => {
              const currentReview = houseCorrectionReview;
              if (currentReview?.neighborhood.id === neighborhoodId) {
                houseCorrectionReview = { ...currentReview, houses: currentReview.houses.map((house) => house.id === houseId ? updatedHouse : house) };
                houseCorrectionNotice = `${updatedHouse.address || 'House'} updated to ${houseConditionLabel(updatedHouse.conditionStatus)}. Map assignment and completion are unchanged.`;
              }
            })
            .catch((error) => {
              if (houseCorrectionReview?.neighborhood.id === neighborhoodId) houseCorrectionNotice = error instanceof Error ? error.message : 'Could not save the house correction.';
            })
            .finally(() => { savingHouseConditions.delete(houseId); renderHouseCorrections(); });
        });
      });
    };
    const updateAssignButton = (): void => {
      if (assignButton) {
        assignButton.disabled = !selectedTarget || selectedSections.length === 0;
        assignButton.textContent = selectedTarget?.type === 'GROUP'
          ? 'Reserve selected sections for group'
          : 'Assign directly to person';
      }
      if (clearSelectedButton) clearSelectedButton.disabled = selectedSections.length === 0;
      allowAdditionalRow?.classList.toggle('hidden', selectedTarget?.type === 'GROUP');
    };
    const updateSectionSummary = (): void => {
      if (summary) {
        const doors = selectedSections.reduce((total, section) => total + section.doors, 0);
        const stackCount = doors > 0 ? Math.ceil(doors / 50) : 0;
        const neighborhoodsSelected = new Set(selectedSections.map((section) => section.neighborhoodId)).size;
        const neighborhoodCopy = neighborhoodsSelected > 1
          ? ` · ${neighborhoodsSelected.toLocaleString()} neighborhoods`
          : '';
        summary.innerHTML = `<strong>${selectedSections.length.toLocaleString()} section${selectedSections.length === 1 ? '' : 's'} selected</strong><span>${doors.toLocaleString()} doors · ${stackCount.toLocaleString()} tract stack${stackCount === 1 ? '' : 's'}${neighborhoodCopy}</span>`;
      }
      updateAssignButton();
    };
    const renderSelectedTarget = (): void => {
      if (!selectedTargetBox) return;
      if (!selectedTarget) {
        selectedTargetBox.innerHTML = '';
        selectedTargetBox.classList.add('hidden');
        updateAssignButton();
        return;
      }
      const detail = selectedTarget.type === 'GROUP'
        ? `<small>Small group · ${selectedTarget.group.memberCount.toLocaleString()} people</small>`
        : '<small>Direct assignment · this person will receive the sections on their personal map</small>';
      selectedTargetBox.innerHTML = `<span>${selectedTarget.type === 'GROUP' ? 'Reserving for' : 'Assigning directly to'}</span><strong>${escapeAdminHtml(selectedTarget.label)}</strong>${detail}<button id="clear-admin-assignment-target" type="button" class="text-button">Change</button>`;
      selectedTargetBox.classList.remove('hidden');
      document.querySelector<HTMLButtonElement>('#clear-admin-assignment-target')?.addEventListener('click', () => {
        selectedTarget = null;
        if (targetInput) targetInput.value = '';
        if (targetResults) targetResults.innerHTML = '';
        renderSelectedTarget();
        targetInput?.focus();
      });
      updateAssignButton();
    };
    const renderTargets = (targets: UnifiedTarget[], message = ''): void => {
      if (!targetResults) return;
      if (message) {
        targetResults.innerHTML = `<div class="person-search-state">${escapeAdminHtml(message)}</div>`;
        return;
      }
      targetResults.innerHTML = targets.map((item) => `
        <button type="button" class="person-search-result" data-admin-unified-target-type="${item.type}" data-admin-unified-target-id="${escapeAdminHtml(item.id)}">
          <strong>${escapeAdminHtml(item.label)}</strong><small>${item.type === 'GROUP' ? `Small group · ${item.group.memberCount.toLocaleString()} people` : 'Person'}</small>
        </button>
      `).join('');
      targetResults.querySelectorAll<HTMLButtonElement>('[data-admin-unified-target-id]').forEach((button) => {
        button.addEventListener('click', () => {
          selectedTarget = targets.find((item) => item.id === button.dataset.adminUnifiedTargetId && item.type === button.dataset.adminUnifiedTargetType) ?? null;
          renderSelectedTarget();
          targetResults.innerHTML = '';
          setMessage('');
        });
      });
    };
    const searchTargets = (): void => {
      const query = targetInput?.value.trim() ?? '';
      const requestSequence = ++searchSequence;
      selectedTarget = null;
      renderSelectedTarget();
      if (query.length < 2) {
        renderTargets([], query ? 'Type at least two letters.' : '');
        return;
      }
      renderTargets([], 'Searching people and small groups…');
      void Promise.all([
        api.searchAdminPeople(adminKey, query),
        api.searchAdminGroups(adminKey, query)
      ]).then(([people, groups]) => {
        if (requestSequence !== searchSequence) return;
        const targets: UnifiedTarget[] = [
          ...people.map((person) => ({ type: 'PERSON' as const, id: person.id, label: person.displayName, person })),
          ...groups.map((group) => ({ type: 'GROUP' as const, id: group.id, label: group.name, group }))
        ].sort((left, right) => left.label.localeCompare(right.label) || left.type.localeCompare(right.type));
        renderTargets(targets, targets.length ? '' : 'No matching person or small group.');
      }).catch((error) => {
        if (requestSequence !== searchSequence) return;
        renderTargets([], error instanceof Error ? error.message : 'Could not search people and groups.');
      });
    };
    const applyMapSearch = (): void => {
      const query = mapSearch?.value.trim().toLocaleLowerCase() ?? '';
      const shown = query
        ? dashboard.sections.filter((section) => sectionSearchText(section).includes(query)).length
        : dashboard.sections.length;
      if (mapCount) mapCount.textContent = `${shown.toLocaleString()} section${shown === 1 ? '' : 's'} shown`;
      if (currentMode === 'ASSIGN') assignmentPickerMap?.setSearch(query);
      else coverageMap?.setSearch(query);
    };
    const unstartedAssignedSectionsForPerson = (
      scope: Extract<CoverageAnalyticsScope, { type: 'PERSON' }>
    ): AdminCoverageSection[] => {
      const scopeAliases = new Set<string>([
        scope.id,
        ...(scope.aliases ?? [])
      ]);
      const normalizedScopeName = scope.normalizedName ?? normalizeAnalyticsName(scope.label);

      return dashboard.sections
        .filter((section) => {
          if (section.status !== 'ASSIGNED' || section.startedAt || !section.assignedTo) return false;

          const assignedAliases = [
            section.assignedTo.id,
            section.assignedTo.pcoPersonId
          ].filter((value): value is string => Boolean(value));

          if (assignedAliases.some((value) => scopeAliases.has(value))) return true;

          return normalizeAnalyticsName(section.assignedTo.displayName) === normalizedScopeName;
        })
        .sort((left, right) =>
          left.neighborhoodName.localeCompare(right.neighborhoodName) ||
          left.sectionNumber - right.sectionNumber
        );
    };

    const renderPersonUnstartedMaps = (scope: CoverageAnalyticsScope): void => {
      if (!personUnstartedMaps) return;

      if (scope.type !== 'PERSON') {
        personUnstartedMaps.innerHTML = '';
        personUnstartedMaps.classList.add('hidden');
        return;
      }

      const sections = unstartedAssignedSectionsForPerson(scope);
      personUnstartedMaps.classList.remove('hidden');

      if (!sections.length) {
        personUnstartedMaps.innerHTML = `
          <div class="simple-explore-heading">
            <div>
              <strong>Maps not started</strong>
              <span>${escapeAdminHtml(scope.label)} has no assigned maps waiting to be started.</span>
            </div>
          </div>
        `;
        return;
      }

      personUnstartedMaps.innerHTML = `
        <div class="simple-explore-heading">
          <div>
            <strong>Maps not started</strong>
            <span>${sections.length.toLocaleString()} assigned map${sections.length === 1 ? '' : 's'} ready to print for ${escapeAdminHtml(scope.label)}.</span>
          </div>
          <button id="admin-print-all-unstarted-maps" type="button" class="primary-button compact-button">
            Print All (${sections.length.toLocaleString()})
          </button>
        </div>

        <div class="coverage-scope-picker">
          ${sections.map((section) => `
            <div class="coverage-scope-current">
              <div>
                <strong>${escapeAdminHtml(section.neighborhoodName)} · Section ${section.sectionNumber.toLocaleString()}</strong>
                <span>${section.doors.toLocaleString()} doors${section.streetNames.length ? ` · ${escapeAdminHtml(section.streetNames.join(', '))}` : ''}</span>
              </div>
              <button
                type="button"
                class="secondary-button compact-button"
                data-admin-print-unstarted-section="${escapeAdminHtml(section.id)}"
              >
                Print
              </button>
            </div>
          `).join('')}
        </div>
      `;

      personUnstartedMaps
        .querySelector<HTMLButtonElement>('#admin-print-all-unstarted-maps')
        ?.addEventListener('click', () => {
          openPersonUnfinishedMapsPrint(
            scope.label,
            sections,
            dashboard.sections,
            'UNSTARTED'
          );
        });

      personUnstartedMaps
        .querySelectorAll<HTMLButtonElement>('[data-admin-print-unstarted-section]')
        .forEach((button) => {
          button.addEventListener('click', () => {
            const sectionId = button.dataset.adminPrintUnstartedSection ?? '';
            const section = sections.find((candidate) => candidate.id === sectionId);
            if (!section) return;

            openPersonUnfinishedMapsPrint(
              scope.label,
              [section],
              dashboard.sections,
              'UNSTARTED'
            );
          });
        });
    };

    const chooseScope = (scope: CoverageAnalyticsScope): void => {
      currentScope = scope;
      const metrics = coverageMap?.setScope(scope) ?? coverageMetricsForScope(dashboard.sections, scope);
      if (scopeMetrics) scopeMetrics.innerHTML = coverageMetricsTemplate(metrics, scope.type);
      if (scopeCurrent) scopeCurrent.innerHTML = `Showing <strong>${escapeAdminHtml(scope.label)}</strong>`;
      if (scopeInput) scopeInput.value = scope.type === 'OVERALL' ? '' : scope.label;
      scopeResults?.classList.add('hidden');
      renderPersonUnstartedMaps(scope);
    };
    const renderScopes = (): void => {
      if (!scopeResults) return;
      const query = normalizeAnalyticsName(scopeInput?.value ?? '');
      if (!query) {
        scopeResults.innerHTML = '';
        scopeResults.classList.add('hidden');
        return;
      }
      const matches = scopes
        .filter((scope) => scope.type !== 'OVERALL' && normalizeAnalyticsName(scope.label).includes(query))
        .slice(0, 30);
      scopeResults.innerHTML = matches.length
        ? matches.map((scope) => `<button type="button" data-coverage-scope-type="${scope.type}" data-coverage-scope-id="${escapeAdminHtml(scope.id)}"><strong>${escapeAdminHtml(scope.label)}</strong><small>${scope.type === 'GROUP' ? 'Small group' : 'Person'}</small></button>`).join('')
        : '<div class="person-search-state">No matching group or person.</div>';
      scopeResults.classList.remove('hidden');
      scopeResults.querySelectorAll<HTMLButtonElement>('[data-coverage-scope-id]').forEach((button) => {
        button.addEventListener('click', () => {
          const scope = scopes.find((candidate) => candidate.id === button.dataset.coverageScopeId && candidate.type === button.dataset.coverageScopeType);
          if (scope) chooseScope(scope);
        });
      });
    };
    const mountAssignmentView = (): void => {
      currentMode = 'ASSIGN';
      coverageMap?.destroy();
      coverageMap = null;
      assignmentPickerMap?.destroy();
      assignmentPickerMap = mountSectionPickerMap(
        'admin-master-coverage-map',
        dashboard,
        (next) => {
          selectedSections = next;
          updateSectionSummary();
        },
        100,
        { showUnavailable: true, initialSelectedIds: selectedSections.map((section) => section.id), collapseUnavailableStatuses: true }
      );
      assignmentControls?.classList.remove('hidden');
      assignmentFinal?.classList.remove('hidden');
      analyticsControls?.classList.add('hidden');
      if (mapSearchRow && assignmentSearchSlot && mapSearchRow.parentElement !== assignmentSearchSlot) {
        assignmentSearchSlot.append(mapSearchRow);
      }
      assignModeButton?.classList.add('active');
      analyticsModeButton?.classList.remove('active');
      assignModeButton?.setAttribute('aria-pressed', 'true');
      analyticsModeButton?.setAttribute('aria-pressed', 'false');
      selectedLegend?.classList.remove('hidden');
      completedLegend?.classList.add('hidden');
      if (modeDescription) modeDescription.textContent = 'The whole-church published map is shown. Click any gray available section, then search for a person or small group to assign it.';
      if (mapSearch) mapSearch.placeholder = 'Search a neighborhood, street, or section';
      applyMapSearch();
      updateSectionSummary();
    };
    const mountAnalyticsView = (): void => {
      currentMode = 'ANALYTICS';
      assignmentPickerMap?.destroy();
      assignmentPickerMap = null;
      coverageMap?.destroy();
      coverageMap = mountAdminCoverageMap('admin-master-coverage-map', dashboard, {
        initialScope: currentScope,
        canReturnSection: (section) => section.status === 'ASSIGNED' && Boolean(section.assignmentId || section.reservedForGroup),
        onReturnSection: (section) => takeBackAdminSection(section)
      });
      assignmentControls?.classList.add('hidden');
      assignmentFinal?.classList.add('hidden');
      analyticsControls?.classList.remove('hidden');
      if (mapSearchRow && analyticsSearchSlot && mapSearchRow.parentElement !== analyticsSearchSlot) {
        analyticsSearchSlot.append(mapSearchRow);
      }
      assignModeButton?.classList.remove('active');
      analyticsModeButton?.classList.add('active');
      assignModeButton?.setAttribute('aria-pressed', 'false');
      analyticsModeButton?.setAttribute('aria-pressed', 'true');
      selectedLegend?.classList.add('hidden');
      completedLegend?.classList.remove('hidden');
      if (modeDescription) modeDescription.textContent = 'View published-section progress for the whole church, a small group, or a person. Click a section for its details.';
      if (mapSearch) mapSearch.placeholder = 'Find a section, street, neighborhood, group, or person';
      chooseScope(currentScope);
      applyMapSearch();
    };

    targetInput?.addEventListener('input', () => {
      window.clearTimeout(searchTimer);
      searchTimer = window.setTimeout(searchTargets, 250);
    });
    mapSearch?.addEventListener('input', applyMapSearch);
    scopeInput?.addEventListener('input', renderScopes);
    scopeInput?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') scopeResults?.classList.add('hidden');
    });
    scopeClear?.addEventListener('click', () => chooseScope(overallScope));
    houseCorrectionSearch?.addEventListener('input', () => { houseCorrectionNotice = ''; renderHouseCorrections(); });
    houseCorrectionNeighborhood?.addEventListener('change', () => {
      const neighborhoodId = houseCorrectionNeighborhood.value;
      const requestSequence = ++houseCorrectionLoadSequence;
      houseCorrectionReview = null;
      houseCorrectionNotice = '';
      if (houseCorrectionSearch) {
        houseCorrectionSearch.disabled = !neighborhoodId;
        houseCorrectionSearch.value = '';
        houseCorrectionSearch.placeholder = neighborhoodId ? 'Search house number or street' : 'Choose a neighborhood first';
      }
      renderHouseCorrections();
      if (!neighborhoodId) return;
      houseCorrectionNotice = 'Loading houses and No Soliciting markers…';
      renderHouseCorrections();
      void api.getAdminReview(adminKey, neighborhoodId)
        .then((loadedReview) => {
          if (requestSequence !== houseCorrectionLoadSequence) return;
          houseCorrectionReview = loadedReview;
          houseCorrectionNotice = '';
          renderHouseCorrections();
        })
        .catch((error) => {
          if (requestSequence !== houseCorrectionLoadSequence) return;
          houseCorrectionNotice = error instanceof Error ? error.message : 'Could not load houses for this neighborhood.';
          renderHouseCorrections();
        });
    });
    assignModeButton?.addEventListener('click', mountAssignmentView);
    analyticsModeButton?.addEventListener('click', mountAnalyticsView);
    clearSelectedButton?.addEventListener('click', () => assignmentPickerMap?.clear());

    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!selectedTarget) {
        setMessage('Select a person or small group from the search results.', true);
        return;
      }
      if (!selectedSections.length) {
        setMessage('Click at least one gray available section on the map.', true);
        return;
      }
      if (selectedTarget.type === 'PERSON' && !window.confirm(`Assign ${selectedSections.length.toLocaleString()} section${selectedSections.length === 1 ? '' : 's'} directly to ${selectedTarget.label}'s personal map? This will not reserve them for a group. If a group leader should distribute them, choose the group instead.`)) return;
      if (assignButton) {
        assignButton.disabled = true;
        assignButton.textContent = selectedTarget.type === 'PERSON' ? 'Assigning…' : 'Reserving…';
      }
      setMessage('');
      const selectedTargetSnapshot = selectedTarget;
      const sectionsSnapshot = [...selectedSections];
      const routeIds = sectionsSnapshot.map((section) => section.id);

      const operation = selectedTargetSnapshot.type === 'GROUP'
        ? api.reserveAdminSectionsForGroup(adminKey, selectedTargetSnapshot.group.id, routeIds)
            .then((created) => `Reserved ${created.reservedRouteIds.length.toLocaleString()} section${created.reservedRouteIds.length === 1 ? '' : 's'} for ${created.groupName}. Its leaders can now assign them.`)
        : (async (): Promise<string> => {
            const byNeighborhood = new Map<string, AdminCoverageSection[]>();
            for (const section of sectionsSnapshot) {
              const list = byNeighborhood.get(section.neighborhoodId) ?? [];
              list.push(section);
              byNeighborhood.set(section.neighborhoodId, list);
            }
            let assignedCount = 0;
            let personName = selectedTargetSnapshot.person.displayName;
            let batchIndex = 0;
            for (const [neighborhoodId, sections] of byNeighborhood) {
              const created = await api.assignAdminMaps(adminKey, {
                personId: selectedTargetSnapshot.person.id,
                neighborhoodId,
                requestedStacks: Math.ceil(sections.reduce((total, section) => total + section.doors, 0) / 50),
                selectedSectionIds: sections.map((section) => section.id),
                allowAdditional: document.querySelector<HTMLInputElement>('#admin-allow-additional')?.checked === true || batchIndex > 0
              });
              assignedCount += created.sections.length;
              personName = created.checkedOutBy.displayName;
              batchIndex += 1;
            }
            return `Assigned ${assignedCount.toLocaleString()} section${assignedCount === 1 ? '' : 's'} to ${personName}. They will see them when they sign in.`;
          })();

      void operation.then(async (successText) => {
        [neighborhoods, masterCoverage, bootstrap] = await Promise.all([
          api.getAdminNeighborhoods(adminKey),
          api.getAdminCoverage(adminKey),
          api.getVolunteerBootstrap()
        ]);
        showNeighborhoods();
        const refreshedMessage = document.querySelector<HTMLDivElement>('#admin-assignment-message');
        if (refreshedMessage) {
          refreshedMessage.textContent = successText;
          refreshedMessage.classList.remove('hidden', 'error');
          refreshedMessage.classList.add('success');
        }
      }).catch(async (error) => {
        const message = error instanceof Error ? error.message : 'Could not update these sections.';
        setMessage(message, true);
        const reservationNeedsRefresh = error instanceof CoverageApiError && [
          'SECTIONS_UNAVAILABLE',
          'SECTIONS_CHANGED',
          'RESERVATION_FAILED'
        ].includes(error.code ?? '');
        if (reservationNeedsRefresh) {
          try {
            masterCoverage = await api.getAdminCoverage(adminKey);
            dashboard = masterCoverage;
            selectedSections = [];
            mountAssignmentView();
            setMessage(`${message} The map has been refreshed.`, true);
            return;
          } catch {
            // Keep the original reservation error visible if refreshing also fails.
          }
        }
        updateAssignButton();
      });
    });

    renderSelectedTarget();
    chooseScope(overallScope);
    mountAssignmentView();

    let backgroundRefreshPending = false;
    coverageRefreshTimer = window.setInterval(() => {
      if (document.visibilityState !== 'visible' || currentMode !== 'ANALYTICS' || backgroundRefreshPending) return;
      backgroundRefreshPending = true;
      void api.getAdminCoverage(adminKey).then((latest) => {
        if (currentMode !== 'ANALYTICS') return;
        dashboard = latest;
        masterCoverage = latest;
        mountAnalyticsView();
      }).catch(() => {
        // Keep the current map usable; the next background refresh will retry.
      }).finally(() => {
        backgroundRefreshPending = false;
      });
    }, 60_000);
  }

  function wireSelfRegistrationReview(): void {
    const button = document.querySelector<HTMLButtonElement>('#load-self-registrations');
    const list = document.querySelector<HTMLDivElement>('#self-registration-list');
    const message = document.querySelector<HTMLDivElement>('#self-registration-message');
    if (!button || !list) return;
    const load = async (): Promise<void> => {
      button.disabled = true;
      button.textContent = 'Checking…';
      try {
        const people = await api.getSelfRegistrations(adminKey);
        if (!people.length) {
          list.innerHTML = '<div class="person-search-state">No new self-registered volunteers.</div>';
          return;
        }
        list.innerHTML = people.map((person) => `
          <article class="access-person-card" data-registration-id="${escapeAdminHtml(person.id)}">
            <div class="access-person-heading"><div><strong>${escapeAdminHtml(person.displayName)}</strong><small>Verified phone ending in ${escapeAdminHtml(person.phoneEnding || '••')}</small></div></div>
            <div class="access-role-row"><div><small>Added ${escapeAdminHtml(new Date(person.submittedAt).toLocaleString())}. Check Planning Center and add them there if appropriate.</small></div><button type="button" class="secondary-button compact-button" data-registration-reviewed>Mark reviewed</button></div>
          </article>
        `).join('');
        list.querySelectorAll<HTMLButtonElement>('[data-registration-reviewed]').forEach((reviewButton) => {
          reviewButton.addEventListener('click', () => {
            const id = reviewButton.closest<HTMLElement>('[data-registration-id]')?.dataset.registrationId;
            if (!id) return;
            reviewButton.disabled = true;
            reviewButton.textContent = 'Saving…';
            void api.markSelfRegistrationReviewed(adminKey, id).then(load).catch((error) => {
              if (message) { message.textContent = error instanceof Error ? error.message : 'Could not mark this sign-in reviewed.'; message.classList.remove('hidden'); }
              reviewButton.disabled = false;
              reviewButton.textContent = 'Mark reviewed';
            });
          });
        });
        if (message) message.classList.add('hidden');
      } catch (error) {
        list.innerHTML = `<div class="person-search-state">${escapeAdminHtml(error instanceof Error ? error.message : 'Could not load self-registered volunteers.')}</div>`;
      } finally {
        button.disabled = false;
        button.textContent = 'Check for new sign-ins';
      }
    };
    button.addEventListener('click', () => { void load(); });
    void load();
  }

  function wireAccessManagement(): void {
    const input = document.querySelector<HTMLInputElement>('#admin-access-search');
    const results = document.querySelector<HTMLDivElement>('#admin-access-results');
    const message = document.querySelector<HTMLDivElement>('#admin-access-message');
    if (!input || !results) return;
    let timer = 0;
    let sequence = 0;
    let currentPeople: AccessManagementPerson[] = [];

    const setMessage = (text: string, error = false): void => {
      if (!message) return;
      message.textContent = text;
      message.classList.toggle('hidden', !text);
      message.classList.toggle('error', error);
    };
    const sourceLabel = (person: AccessManagementPerson, role: 'LEADER' | 'ADMIN'): string => {
      if (person.automaticRoles.includes(role)) return role === 'LEADER' ? 'Automatic from Planning Center' : 'Protected initial admin';
      if (person.manualRoles.includes(role)) return 'Granted manually';
      return 'Not granted';
    };
    const render = (people: AccessManagementPerson[], state = ''): void => {
      currentPeople = people;
      if (state) {
        results.innerHTML = `<div class="person-search-state">${escapeAdminHtml(state)}</div>`;
        return;
      }
      results.innerHTML = people.map((person) => {
        const automaticLeader = person.automaticRoles.includes('LEADER');
        const manualLeader = person.manualRoles.includes('LEADER');
        const automaticAdmin = person.automaticRoles.includes('ADMIN');
        const manualAdmin = person.manualRoles.includes('ADMIN');
        return `
          <article class="access-person-card" data-access-person-id="${escapeAdminHtml(person.id)}">
            <div class="access-person-heading"><div><strong>${escapeAdminHtml(person.displayName)}</strong><small>Volunteer access · always available</small></div><span>${escapeAdminHtml(person.roles.join(' · '))}</span></div>
            <div class="access-role-row"><div><strong>Leader</strong><small>${escapeAdminHtml(sourceLabel(person, 'LEADER'))}</small></div><button type="button" class="secondary-button compact-button" data-access-role="LEADER" data-access-enabled="${manualLeader ? 'true' : 'false'}" ${automaticLeader ? 'disabled' : ''}>${automaticLeader ? 'Automatic' : manualLeader ? 'Remove leader' : 'Grant leader'}</button></div>
            <div class="access-role-row"><div><strong>Admin</strong><small>${escapeAdminHtml(sourceLabel(person, 'ADMIN'))}</small></div><button type="button" class="secondary-button compact-button" data-access-role="ADMIN" data-access-enabled="${manualAdmin ? 'true' : 'false'}" ${automaticAdmin ? 'disabled' : ''}>${automaticAdmin ? 'Protected' : manualAdmin ? 'Remove admin' : 'Grant admin'}</button></div>
          </article>
        `;
      }).join('');
      results.querySelectorAll<HTMLButtonElement>('[data-access-role]').forEach((button) => {
        button.addEventListener('click', () => {
          const card = button.closest<HTMLElement>('[data-access-person-id]');
          const personId = card?.dataset.accessPersonId ?? '';
          const role = button.dataset.accessRole === 'ADMIN' ? 'ADMIN' : 'LEADER';
          const currentlyEnabled = button.dataset.accessEnabled === 'true';
          const person = currentPeople.find((candidate) => candidate.id === personId);
          if (!person) return;
          if (currentlyEnabled && role === 'ADMIN' && person.id === identitySession.person.id && !window.confirm('Remove your own admin access? This action is blocked by the server, but another admin can remove it.')) return;
          button.disabled = true;
          button.textContent = currentlyEnabled ? 'Removing…' : 'Granting…';
          setMessage('');
          void api.setAdminAccess(adminKey, personId, role, !currentlyEnabled).then((changed) => {
            currentPeople = currentPeople.map((candidate) => candidate.id === changed.person.id ? changed.person : candidate);
            render(currentPeople);
            setMessage(`${changed.person.displayName}: ${role.toLocaleLowerCase()} access ${changed.enabled ? 'granted' : 'removed'}.`);
          }).catch((error) => {
            render(currentPeople);
            setMessage(error instanceof Error ? error.message : 'Could not change access.', true);
          });
        });
      });
    };
    const search = (): void => {
      const query = input.value.trim();
      const requestSequence = ++sequence;
      if (query.length < 2) {
        render([], query ? 'Type at least two letters.' : 'Type at least two letters to manage access.');
        return;
      }
      render([], 'Searching…');
      void api.searchAdminAccessPeople(adminKey, query).then((people) => {
        if (requestSequence !== sequence) return;
        render(people, people.length ? '' : 'No matching Planning Center person.');
      }).catch((error) => {
        if (requestSequence !== sequence) return;
        render([], error instanceof Error ? error.message : 'Could not search access records.');
      });
    };
    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(search, 250);
    });
  }

  async function takeBackAdminSection(section: AdminCoverageSection): Promise<void> {
    if (section.assignmentId) {
      if (!window.confirm(`Take back ${section.neighborhoodName} section ${section.sectionNumber} from ${section.assignedTo?.displayName ?? 'this assignment'}? It will become available to assign again.`)) return;
      await api.releaseAdminSections(adminKey, section.assignmentId, [section.id]);
    } else if (section.reservedForGroup) {
      if (!window.confirm(`Return ${section.neighborhoodName} section ${section.sectionNumber} from ${section.groupName ?? 'this small group'} to the whole-church pool?`)) return;
      await api.releaseAdminGroupReservations(adminKey, [section.id]);
    } else return;
    [neighborhoods, bootstrap, masterCoverage] = await Promise.all([
      api.getAdminNeighborhoods(adminKey),
      api.getVolunteerBootstrap(),
      api.getAdminCoverage(adminKey)
    ]);
    showNeighborhoods();
  }

  function adminAnalyticsScopes(dashboard: AdminCoverageDashboard): CoverageAnalyticsScope[] {
    const scopes: CoverageAnalyticsScope[] = [{ type: 'OVERALL', id: 'OVERALL', label: 'Whole church' }];
    const groups = new Map<string, string>();
    const people = new Map<string, { label: string; normalizedName: string; aliases: Set<string> }>();

    const addPerson = (label: string, ...ids: Array<string | undefined>): void => {
      const normalizedName = normalizeAnalyticsName(label);
      if (!normalizedName) return;
      const person = people.get(normalizedName) ?? { label, normalizedName, aliases: new Set<string>() };
      ids.filter((id): id is string => Boolean(id)).forEach((id) => person.aliases.add(id));
      people.set(normalizedName, person);
    };

    dashboard.sections.forEach((section) => {
      if (section.groupId && section.groupName) groups.set(section.groupId, section.groupName);
      if (section.assignedTo) addPerson(
        section.assignedTo.displayName,
        section.assignedTo.id,
        section.assignedTo.pcoPersonId
      );
      section.teamMembers.forEach((member) => addPerson(member.name, member.personId));
    });

    [...groups.entries()]
      .sort((a, b) => a[1].localeCompare(b[1]))
      .forEach(([id, label]) => scopes.push({ type: 'GROUP', id, label }));
    [...people.values()]
      .sort((a, b) => a.label.localeCompare(b.label))
      .forEach((person) => {
        const aliases = [...person.aliases];
        scopes.push({
          type: 'PERSON',
          id: aliases[0] ?? `NAME:${person.normalizedName}`,
          label: person.label,
          normalizedName: person.normalizedName,
          ...(aliases.length ? { aliases } : {})
        });
      });
    return scopes;
  }


  function wireNeighborhoodRecheck(): void {
    const search = document.querySelector<HTMLInputElement>('#recheck-neighborhood-search');
    const options = Array.from(document.querySelectorAll<HTMLElement>('[data-recheck-search]'));
    const checks = Array.from(document.querySelectorAll<HTMLInputElement>('[data-recheck-neighborhood]'));
    const selectedButton = document.querySelector<HTMLButtonElement>('#recheck-selected-neighborhoods');
    const selectedCount = document.querySelector<HTMLSpanElement>('#recheck-selected-count');
    const allButton = document.querySelector<HTMLButtonElement>('#recheck-all-neighborhoods');

    const updateSelected = (): void => {
      const count = checks.filter((check) => check.checked).length;
      if (selectedCount) selectedCount.textContent = `${count.toLocaleString()} neighborhood${count === 1 ? '' : 's'} selected`;
      if (selectedButton) selectedButton.disabled = count === 0;
    };
    search?.addEventListener('input', () => {
      const query = search.value.trim().toLocaleLowerCase();
      options.forEach((option) => {
        const hidden = Boolean(query) && !(option.dataset.recheckSearch ?? '').includes(query);
        option.hidden = hidden;
        option.classList.toggle('hidden', hidden);
        option.toggleAttribute('data-filter-hidden', hidden);
        option.setAttribute('aria-hidden', hidden ? 'true' : 'false');
        if (hidden) option.style.setProperty('display', 'none', 'important');
        else option.style.removeProperty('display');
      });
    });
    checks.forEach((check) => check.addEventListener('change', updateSelected));
    selectedButton?.addEventListener('click', () => {
      const ids = checks.filter((check) => check.checked).map((check) => check.value);
      if (ids.length) void runNeighborhoodRecheck(ids, false);
    });
    allButton?.addEventListener('click', () => {
      const ids = neighborhoods.filter((neighborhood) => neighborhood.importedHouses > 0).map((neighborhood) => neighborhood.id);
      if (!ids.length) return;
      if (!window.confirm(`Recheck all ${ids.length.toLocaleString()} imported neighborhoods? This can take a while and will not publish anything.`)) return;
      void runNeighborhoodRecheck(ids, true);
    });
    updateSelected();
  }

  async function runNeighborhoodRecheck(ids: string[], all: boolean): Promise<void> {
    const message = document.querySelector<HTMLDivElement>('#neighborhood-recheck-message');
    const buttons = [
      document.querySelector<HTMLButtonElement>('#recheck-all-neighborhoods'),
      document.querySelector<HTMLButtonElement>('#recheck-selected-neighborhoods')
    ].filter((button): button is HTMLButtonElement => Boolean(button));
    buttons.forEach((button) => { button.disabled = true; });
    const results: NeighborhoodRecheckResult[] = [];
    const failures: string[] = [];
    if (message) {
      message.classList.remove('hidden', 'error', 'success');
      message.textContent = `Checking 0 of ${ids.length.toLocaleString()} neighborhoods…`;
    }
    for (let index = 0; index < ids.length; index += 1) {
      const id = ids[index]!;
      if (message) message.textContent = `Checking ${index + 1} of ${ids.length.toLocaleString()}: ${neighborhoods.find((item) => item.id === id)?.name ?? id}…`;
      try {
        results.push(await api.recheckNeighborhood(adminKey, id));
      } catch (error) {
        failures.push(`${neighborhoods.find((item) => item.id === id)?.name ?? id}: ${error instanceof Error ? error.message : 'Recheck failed'}`);
      }
    }
    try {
      [neighborhoods, masterCoverage, bootstrap] = await Promise.all([
        api.getAdminNeighborhoods(adminKey),
        api.getAdminCoverage(adminKey),
        api.getVolunteerBootstrap()
      ]);
      showNeighborhoods();
      const refreshed = document.querySelector<HTMLDivElement>('#neighborhood-recheck-message');
      const pending = results.reduce((sum, result) => sum + result.pendingReviewChanges, 0);
      const added = results.reduce((sum, result) => sum + result.newHouses, 0);
      const changed = results.reduce((sum, result) => sum + result.changedHouses + result.missingFromSource, 0);
      const nonResidential = results.reduce((sum, result) => sum + result.autoExcludedNonResidential, 0);
      const vacantOrConstruction = results.reduce((sum, result) => sum + result.possibleVacantOrConstruction, 0);
      const uncertain = results.reduce((sum, result) => sum + result.uncertainHouses, 0);
      if (refreshed) {
        refreshed.classList.remove('hidden', 'error', 'success');
        refreshed.classList.add(failures.length ? 'error' : 'success');
        refreshed.textContent = `${all ? 'All-neighborhood' : 'Selected-neighborhood'} recheck finished: ${results.length.toLocaleString()} checked, ${added.toLocaleString()} new, ${changed.toLocaleString()} changed or missing, ${nonResidential.toLocaleString()} likely nonresidential, ${vacantOrConstruction.toLocaleString()} possible empty or construction, ${uncertain.toLocaleString()} uncertain, and ${pending.toLocaleString()} total changes awaiting review.${failures.length ? ` ${failures.length} failed: ${failures.slice(0, 3).join(' | ')}` : ''}`;
      }
    } catch (error) {
      if (message) {
        message.classList.add('error');
        message.textContent = error instanceof Error ? error.message : 'The recheck finished, but the dashboard could not refresh.';
      }
      buttons.forEach((button) => { button.disabled = false; });
    }
  }

  function wireSeasonManagement(): void {
    const message = document.querySelector<HTMLDivElement>('#season-management-message');
    const setBusy = (busy: boolean): void => {
      document.querySelectorAll<HTMLButtonElement>('.season-management-card button').forEach((button) => { button.disabled = busy; });
      if (!busy) {
        const closeInput = document.querySelector<HTMLInputElement>('#close-season-confirmation');
        const closeButton = document.querySelector<HTMLButtonElement>('#close-season');
        if (closeButton) closeButton.disabled = closeInput?.value.trim().toUpperCase() !== 'STOP SEASON';
      }
    };
    const finish = async (action: () => Promise<{ season: SeasonState; affectedNeighborhoods: number; affectedRoutes: number; affectedAssignments: number }>, success: string): Promise<void> => {
      setBusy(true);
      if (message) { message.classList.remove('hidden', 'error', 'success'); message.textContent = 'Saving season changes…'; }
      try {
        const result = await action();
        season = result.season;
        [neighborhoods, bootstrap, masterCoverage] = await Promise.all([
          api.getAdminNeighborhoods(adminKey), api.getVolunteerBootstrap(), api.getAdminCoverage(adminKey)
        ]);
        showNeighborhoods();
        const refreshed = document.querySelector<HTMLDivElement>('#season-management-message');
        if (refreshed) {
          refreshed.classList.remove('hidden', 'error');
          refreshed.classList.add('success');
          refreshed.textContent = `${success} ${result.affectedNeighborhoods.toLocaleString()} neighborhoods, ${result.affectedRoutes.toLocaleString()} sections, and ${result.affectedAssignments.toLocaleString()} active assignments were affected.`;
        }
      } catch (error) {
        setBusy(false);
        if (message) {
          message.classList.remove('hidden', 'success');
          message.classList.add('error');
          message.textContent = error instanceof Error ? error.message : 'Could not update the season.';
        }
      }
    };
    const closeInput = document.querySelector<HTMLInputElement>('#close-season-confirmation');
    const closeButton = document.querySelector<HTMLButtonElement>('#close-season');
    closeInput?.addEventListener('input', () => {
      if (closeButton) closeButton.disabled = closeInput.value.trim().toUpperCase() !== 'STOP SEASON';
    });
    closeButton?.addEventListener('click', () => {
      if (!window.confirm('Stop this season, unpublish every neighborhood map, and remove every active assignment from volunteers? Completed work and assignment records will be preserved.')) return;
      void finish(() => api.closeSeason(adminKey, closeInput?.value.trim().toUpperCase() ?? ''), 'The season is stopped. All maps are unpublished and active assignments have been removed from volunteers.');
    });
    document.querySelector<HTMLButtonElement>('#create-season')?.addEventListener('click', () => {
      const name = document.querySelector<HTMLInputElement>('#new-season-name')?.value.trim() ?? '';
      if (!name) {
        if (message) { message.classList.remove('hidden'); message.classList.add('error'); message.textContent = 'Enter the new season name.'; }
        return;
      }
      void finish(() => api.createSeason(adminKey, name), `${name} is active. Neighborhoods remain drafts until published.`);
    });
  }

  function wireAssignmentActivityReset(): void {
    const input = document.querySelector<HTMLInputElement>('#clear-activity-confirmation');
    const button = document.querySelector<HTMLButtonElement>('#clear-assignment-activity');
    const message = document.querySelector<HTMLDivElement>('#clear-activity-message');
    const expected = 'CLEAR TEST ACTIVITY';
    input?.addEventListener('input', () => {
      if (button) button.disabled = input.value.trim().toLocaleUpperCase() !== expected;
    });
    button?.addEventListener('click', async () => {
      const confirmation = input?.value.trim().toLocaleUpperCase() ?? '';
      if (confirmation !== expected) return;
      if (!window.confirm('Permanently delete every assignment and completion record, release all group reservations, and reset section analytics? House review dots and published map geometry will stay.')) return;
      if (button) { button.disabled = true; button.textContent = 'Clearing activity…'; }
      if (message) { message.classList.remove('hidden', 'error', 'success'); message.textContent = 'Clearing assignments and resetting section activity…'; }
      try {
        const result = await api.clearAssignmentActivity(adminKey, confirmation);
        try { window.localStorage.removeItem('buckeyeCoverage2ActiveClaimToken'); } catch { /* ignore */ }
        [neighborhoods, bootstrap, masterCoverage] = await Promise.all([
          api.getAdminNeighborhoods(adminKey), api.getVolunteerBootstrap(), api.getAdminCoverage(adminKey)
        ]);
        showNeighborhoods();
        const refreshed = document.querySelector<HTMLDivElement>('#clear-activity-message');
        if (refreshed) {
          refreshed.classList.remove('hidden', 'error');
          refreshed.classList.add('success');
          refreshed.textContent = `Fresh start complete: ${result.clearedAssignments.toLocaleString()} assignments deleted, ${result.resetSections.toLocaleString()} sections reset, ${result.completedSectionsCleared.toLocaleString()} completed sections cleared, and ${result.groupReservationsReleased.toLocaleString()} group reservations released. Published maps and house-dot data were preserved.`;
        }
      } catch (error) {
        if (button) { button.disabled = false; button.textContent = 'Clear assignments, completion & analytics'; }
        if (message) {
          message.classList.remove('hidden', 'success');
          message.classList.add('error');
          message.textContent = error instanceof Error ? error.message : 'Could not clear assignment activity.';
        }
      }
    });
  }

  async function syncPlanningCenter(quiet: boolean): Promise<void> {
    const button = document.querySelector<HTMLButtonElement>('#sync-planning-center');
    const message = document.querySelector<HTMLDivElement>('#pco-sync-message');
    if (button) { button.disabled = true; button.textContent = 'Syncing…'; }
    if (message) { message.classList.add('hidden'); message.textContent = ''; }
    try {
      const result = await api.syncPlanningCenter(adminKey);
      pcoStatus = result;
      showNeighborhoods();
      const updatedMessage = document.querySelector<HTMLDivElement>('#pco-sync-message');
      if (updatedMessage && !quiet) {
        updatedMessage.textContent = `Synced ${result.syncedPeople.toLocaleString()} people, ${result.syncedLeaders.toLocaleString()} leaders, ${result.syncedGroups.toLocaleString()} groups, and ${result.syncedGroupMemberships.toLocaleString()} group memberships.`;
        updatedMessage.classList.remove('hidden');
      }
    } catch (error) {
      if (button) { button.disabled = false; button.textContent = 'Sync now'; }
      if (message && !quiet) {
        message.textContent = error instanceof Error ? error.message : 'Could not sync Planning Center.';
        message.classList.remove('hidden');
        message.classList.add('error');
      }
    }
  }

  async function maybeAutoSyncPlanningCenter(): Promise<void> {
    if (!pcoStatus?.configured) return;
    const lastSync = pcoStatus.lastSyncedAt ? new Date(pcoStatus.lastSyncedAt).getTime() : 0;
    const stale = !lastSync || Date.now() - lastSync > 24 * 60 * 60 * 1000 || (pcoStatus.peopleCount > 0 && pcoStatus.groupCount === 0);
    if (!stale) return;
    const key = 'buckeyeCoveragePcoAutoSyncAttempt';
    try {
      const lastAttempt = Number(window.sessionStorage.getItem(key) ?? 0);
      if (Date.now() - lastAttempt < 60 * 60 * 1000) return;
      window.sessionStorage.setItem(key, String(Date.now()));
    } catch {
      // Storage restrictions should not prevent a stale directory refresh.
    }
    await syncPlanningCenter(true);
  }

  async function loadCoverage(neighborhoodId: string): Promise<void> {
    disposeMaps();
    root.innerHTML = adminShellTemplate(adminLoadingTemplate('Loading section progress…'));
    wireLogout();
    try {
      neighborhoodCoverage = await api.getAdminCoverage(adminKey, neighborhoodId);
      showCoverage();
    } catch (error) {
      showAdminError(error);
      showNeighborhoods();
    }
  }

  function showCoverage(): void {
    if (!neighborhoodCoverage) return;
    disposeMaps();
    root.innerHTML = adminShellTemplate(adminNeighborhoodCoverageTemplate(neighborhoodCoverage));
    wireLogout();
    document.querySelector<HTMLButtonElement>('#back-to-neighborhoods')?.addEventListener('click', showNeighborhoods);
    document.querySelector<HTMLButtonElement>('#edit-neighborhood-review')?.addEventListener('click', () => {
      if (neighborhoodCoverage?.neighborhoodId) void loadReview(neighborhoodCoverage.neighborhoodId);
    });
    coverageMap = mountAdminCoverageMap('admin-neighborhood-coverage-map', neighborhoodCoverage, {
      canReturnSection: (section) => section.status === 'ASSIGNED' && Boolean(section.assignmentId || section.reservedForGroup),
      onReturnSection: (section) => takeBackAdminSection(section)
    });
    wireCoverageSearch(neighborhoodCoverage, 'neighborhood-coverage-search');

    let backgroundRefreshPending = false;
    const neighborhoodId = neighborhoodCoverage.neighborhoodId;
    coverageRefreshTimer = window.setInterval(() => {
      if (document.visibilityState !== 'visible' || backgroundRefreshPending) return;
      backgroundRefreshPending = true;
      void api.getAdminCoverage(adminKey, neighborhoodId).then((latest) => {
        if (neighborhoodCoverage?.neighborhoodId !== neighborhoodId) return;
        neighborhoodCoverage = latest;
        coverageMap?.destroy();
        coverageMap = mountAdminCoverageMap('admin-neighborhood-coverage-map', latest, {
          canReturnSection: (section) => section.status === 'ASSIGNED' && Boolean(section.assignmentId || section.reservedForGroup),
          onReturnSection: (section) => takeBackAdminSection(section)
        });
        const query = document.querySelector<HTMLInputElement>('#neighborhood-coverage-search')?.value.trim();
        if (query) coverageMap?.setSearch(query);
      }).catch(() => {
        // Leave the current map visible and retry on the next interval.
      }).finally(() => {
        backgroundRefreshPending = false;
      });
    }, 60_000);
  }

  function wireCoverageSearch(dashboard: AdminCoverageDashboard, inputId: string): void {
    const input = document.querySelector<HTMLInputElement>(`#${CSS.escape(inputId)}`);
    const count = document.querySelector<HTMLSpanElement>(`#${CSS.escape(inputId)}-count`);
    const apply = (): void => {
      const query = input?.value.trim().toLocaleLowerCase() ?? '';
      const shown = query
        ? dashboard.sections.filter((section) => sectionSearchText(section).includes(query)).length
        : dashboard.sections.length;
      if (count) count.textContent = `${shown.toLocaleString()} section${shown === 1 ? '' : 's'} shown`;
      coverageMap?.setSearch(query);
    };
    input?.addEventListener('input', apply);
  }

  async function loadReview(neighborhoodId: string): Promise<void> {
    disposeMaps();
    root.innerHTML = adminShellTemplate(adminLoadingTemplate('Loading house dots…'));
    wireLogout();
    try {
      review = await api.getAdminReview(adminKey, neighborhoodId);
      showReview();
    } catch (error) {
      showAdminError(error);
      showNeighborhoods();
    }
  }

  function showReview(): void {
    if (!review) return;
    disposeMaps();
    workflowBusy = false;
    root.innerHTML = adminShellTemplate(adminReviewTemplate(review));
    wireLogout();

    document.querySelector<HTMLButtonElement>('#back-to-neighborhoods')?.addEventListener('click', showNeighborhoods);
    document.querySelector<HTMLButtonElement>('#publish-neighborhood')?.addEventListener('click', () => {
      void publishNeighborhood();
    });
    wirePublishWorkflow();

    reviewMap = mountAdminReviewMap(review, {
      onBulkChange: async (houses, nextStatus) => {
        try {
          const updated = await api.setHousesInclusion(
            adminKey,
            review!.neighborhood.id,
            houses.map((house) => house.id),
            nextStatus
          );
          applyHouseUpdates(updated);
          setAdminMessage(
            nextStatus === 'EXCLUDED'
              ? `${updated.length.toLocaleString()} selected dot${updated.length === 1 ? '' : 's'} excluded.`
              : `${updated.length.toLocaleString()} selected house${updated.length === 1 ? '' : 's'} restored.`,
            false
          );
          return updated;
        } catch (error) {
          setAdminMessage(error instanceof Error ? error.message : 'Could not update the selected dots.', true);
          throw error;
        }
      },
      onMatchStreet: async (houses, streetUnit, method) => {
        const results: AdminHouseStreetMatchResult[] = [];
        try {
          // Run these sequentially so each response contains an accurate remaining-unmatched count.
          for (const house of houses) {
            results.push(await api.matchHouseToStreet(
              adminKey,
              review!.neighborhood.id,
              house.id,
              streetUnit.id,
              method
            ));
          }
          return results;
        } catch (error) {
          setAdminMessage(
            error instanceof Error
              ? `${results.length.toLocaleString()} of ${houses.length.toLocaleString()} houses were connected before the update stopped: ${error.message}`
              : 'Could not connect the selected houses to the street.',
            true
          );
          throw error;
        }
      },
      onCreateManualStreet: async (house) => {
        try {
          return await api.createManualStreetForHouse(adminKey, review!.neighborhood.id, house.id);
        } catch (error) {
          setAdminMessage(error instanceof Error ? error.message : 'Could not create the manual street segment.', true);
          throw error;
        }
      },
      onStreetMatchesApplied: (results) => applyStreetMatchResultsToReview(results)
    });
    wireSimpleReviewActions();
    updateSimpleReviewReadiness();
  }

  function wireSimpleReviewActions(): void {
    document.querySelector<HTMLButtonElement>('#review-next-action')?.addEventListener('click', () => {
      if (!review || !reviewMap) return;
      const pending = review.neighborhood.pendingReviewChanges ?? 0;
      if (pending > 0) reviewMap.setFilter('CHANGES');
      else if (review.neighborhood.unmatchedIncludedHouses > 0) reviewMap.setFilter('UNMATCHED');
      document.querySelector<HTMLElement>('#admin-review-map')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  function updateSimpleReviewReadiness(): void {
    if (!review) return;
    const pending = review.neighborhood.pendingReviewChanges ?? 0;
    const unmatched = review.neighborhood.unmatchedIncludedHouses;
    const readiness = document.querySelector<HTMLElement>('#review-readiness');
    const title = document.querySelector<HTMLElement>('#review-readiness-title');
    const detail = document.querySelector<HTMLElement>('#review-readiness-detail');
    const nextAction = document.querySelector<HTMLButtonElement>('#review-next-action');

    readiness?.classList.toggle('needs-work', pending > 0 || unmatched > 0);
    readiness?.classList.toggle('ready', pending === 0 && unmatched === 0);
    const icon = readiness?.querySelector<HTMLElement>('.simple-readiness-icon');
    if (icon) icon.textContent = pending > 0 || unmatched > 0 ? '!' : '✓';

    if (pending > 0) {
      if (title) title.textContent = `${pending.toLocaleString()} house${pending === 1 ? '' : 's'} need a decision`;
      if (detail) detail.textContent = 'Review each yellow house and choose Include or Exclude.';
      if (nextAction) { nextAction.textContent = 'Review changes'; nextAction.classList.remove('hidden'); }
    } else if (unmatched > 0) {
      if (title) title.textContent = `${unmatched.toLocaleString()} house${unmatched === 1 ? '' : 's'} need a street`;
      if (detail) detail.textContent = 'Select one or several houses and connect them to the correct street.';
      if (nextAction) { nextAction.textContent = 'Fix street matches'; nextAction.classList.remove('hidden'); }
    } else {
      if (title) title.textContent = 'Ready to publish';
      if (detail) detail.textContent = 'Every included house is reviewed and connected to a street.';
      nextAction?.classList.add('hidden');
    }

    const setStep = (id: string, state: 'active' | 'done' | 'waiting', text: string): void => {
      const step = document.querySelector<HTMLElement>(`#${id}`);
      if (!step) return;
      step.classList.remove('active', 'done');
      if (state !== 'waiting') step.classList.add(state);
      const small = step.querySelector<HTMLElement>('small');
      if (small) small.textContent = text;
    };
    setStep('review-step-changes', pending > 0 ? 'active' : 'done', pending > 0 ? `${pending.toLocaleString()} remaining` : 'Done');
    setStep('review-step-streets', pending === 0 && unmatched > 0 ? 'active' : unmatched === 0 ? 'done' : 'waiting', unmatched > 0 ? `${unmatched.toLocaleString()} remaining` : 'Done');
    setStep('review-step-publish', pending === 0 && unmatched === 0 ? 'active' : 'waiting', pending === 0 && unmatched === 0 ? 'Ready' : 'Waiting');
  }

  function applyStreetMatchResultsToReview(results: AdminHouseStreetMatchResult[]): void {
    if (!review || !results.length) return;
    const updatedById = new Map(results.map((result) => [result.house.id, result.house]));
    review.houses = review.houses.map((house) => updatedById.get(house.id) ?? house);
    for (const result of results) {
      if (result.createdStreetUnit && !review.streetUnits.some((unit) => unit.id === result.streetUnit.id)) {
        review.streetUnits = [...review.streetUnits, result.streetUnit];
      }
    }
    const finalResult = results[results.length - 1]!;
    review.neighborhood.unmatchedIncludedHouses = finalResult.unmatchedIncludedHouses;
    refreshReviewCounts();
    setAdminMessage(
      results.length === 1
        ? finalResult.createdStreetUnit
          ? `${finalResult.house.address || 'House'} is now connected through a manual street segment.`
          : `${finalResult.house.address || 'House'} is now connected to ${finalResult.streetUnit.displayStreet}.`
        : `${results.length.toLocaleString()} selected houses are now connected to ${finalResult.streetUnit.displayStreet}.`,
      false
    );
  }

  function applyHouseUpdates(updatedHouses: ReviewHouse[]): void {
    if (!review) return;
    const updatedById = new Map(updatedHouses.map((house) => [house.id, house]));
    review.houses = review.houses.map((house) => updatedById.get(house.id) ?? house);
    reviewMap?.updateHouses(updatedHouses);
    refreshReviewCounts();
  }

  function refreshReviewCounts(): void {
    if (!review) return;
    const includedHouses = review.houses.filter((house) => house.inclusionStatus === 'INCLUDED');
    review.neighborhood.includedHouses = includedHouses.length;
    review.neighborhood.excludedHouses = review.houses.length - includedHouses.length;
    review.neighborhood.unmatchedIncludedHouses = includedHouses.filter(
      (house) => house.matchStatus === 'UNMATCHED' || !house.unitId
    ).length;
    review.neighborhood.pendingReviewChanges = review.houses.filter((house) => house.reviewRequired === true).length;
    if (review.neighborhood.pendingReviewChanges === 0) reviewMap?.setChangesOnly(false);

    const metrics = document.querySelector<HTMLDivElement>('#review-metrics');
    if (metrics) metrics.innerHTML = reviewMetricsTemplate(review);

    const publishButton = document.querySelector<HTMLButtonElement>('#publish-neighborhood');
    if (publishButton) {
      publishButton.disabled = review.neighborhood.unmatchedIncludedHouses > 0
        || (review.neighborhood.pendingReviewChanges ?? 0) > 0
        || workflowBusy;
    }

    document.querySelectorAll<HTMLElement>('.review-checklist strong').forEach((element, elementIndex) => {
      const values = [
        review!.neighborhood.includedHouses,
        review!.neighborhood.excludedHouses,
        review!.neighborhood.screeningExcludedHouses ?? 0,
        review!.neighborhood.screeningReviewHouses ?? 0,
        review!.neighborhood.unmatchedIncludedHouses
      ];
      element.textContent = String(values[elementIndex] ?? 0);
    });
    updateSimpleReviewReadiness();
  }

  async function publishNeighborhood(): Promise<void> {
    if (!review || workflowBusy) return;
    workflowBusy = true;
    openPublishWorkflow();
    resetPublishWorkflow();
    setWorkflowStep('review', 'active');
    setWorkflowText('Checking reviewed houses', 'Confirming that every included dot is matched before sections are built.');
    setPrimaryPublishBusy(true);

    try {
      if (review.neighborhood.unmatchedIncludedHouses > 0) {
        throw new Error('Unmatched green dots must be excluded or corrected before publishing.');
      }
      setWorkflowStep('review', 'done');
      setWorkflowStep('reset', 'skipped');
      setWorkflowStep('build', 'active');
      setWorkflowText('Building balanced sections', 'Keeping nearby houses together and avoiding tiny leftover pockets.');
      const result = await api.publishNeighborhood(adminKey, review.neighborhood.id);
      setWorkflowStep('build', 'done');
      finishPublishSuccess(result);
    } catch (error) {
      if (
        error instanceof CoverageApiError &&
        error.code === 'EXISTING_ASSIGNMENTS' &&
        review.neighborhood.developmentResetAllowed
      ) {
        setWorkflowStep('review', 'done');
        setWorkflowStep('reset', 'action');
        setWorkflowStep('build', 'waiting');
        setWorkflowText(
          'Development reset required',
          'Confirm once below. The admin will delete the old test assignments and immediately rebuild the sections—no third click.'
        );
        showResetConfirmation(true);
        workflowBusy = false;
        setPrimaryPublishBusy(false);
        return;
      }
      setWorkflowStep('review', 'error');
      setWorkflowStep('build', 'error');
      finishPublishError(error);
    }
  }

  async function resetAndRebuild(): Promise<void> {
    if (!review || workflowBusy) return;
    const input = document.querySelector<HTMLInputElement>('#publish-reset-input');
    if (input?.value.trim().toUpperCase() !== 'RESET') return;

    workflowBusy = true;
    setPrimaryPublishBusy(true);
    setWorkflowStep('reset', 'active');
    setWorkflowStep('build', 'waiting');
    setWorkflowText(
      'Resetting and rebuilding automatically',
      'Old development assignments are being removed. Your reviewed house dots and exclusions are preserved.'
    );
    setResetControlsDisabled(true);

    try {
      const result = await api.rebuildDevelopmentNeighborhood(
        adminKey,
        review.neighborhood.id,
        `${review.neighborhood.id}:RESET`
      );
      setWorkflowStep('reset', 'done');
      setWorkflowStep('build', 'done');
      finishPublishSuccess(result, `Deleted ${result.deletedAssignments.toLocaleString()} old assignment${result.deletedAssignments === 1 ? '' : 's'} and ${result.deletedSections.toLocaleString()} old section${result.deletedSections === 1 ? '' : 's'}. `);
    } catch (error) {
      setWorkflowStep('reset', 'error');
      setWorkflowStep('build', 'error');
      finishPublishError(error);
    }
  }

  function finishPublishSuccess(result: AdminPublishResult, prefix = ''): void {
    if (!review) return;
    review.neighborhood.workflowStatus = 'PUBLISHED';
    review.neighborhood.published = true;
    review.neighborhood.generatedSections = result.generatedSections;
    review.neighborhood.availableSections = result.availableSections;
    refreshReviewCounts();

    const status = document.querySelector<HTMLDivElement>('.review-status-pill');
    if (status) status.textContent = 'PUBLISHED';
    const summary = publishSuccessTemplate(
      result.generatedSections,
      result.includedHouses,
      result.minimumSectionDoors,
      result.maximumSectionDoors,
      result.averageSectionDoors
    );
    setAdminMessage(summary, false, true);
    setWorkflowText('Neighborhood published', 'The reviewed houses are now available as compact volunteer sections. Refreshing the admin page with the latest map…');
    setWorkflowResult(`${prefix}${summary}<br><small>The admin page will refresh automatically.</small>`, false, true);
    showResetConfirmation(false);
    showWorkflowFinishButton(false);
    workflowBusy = false;
    setPrimaryPublishBusy(false);
    updatePrimaryPublishLabel();
    window.setTimeout(() => window.location.reload(), 1200);
  }

  function finishPublishError(error: unknown): void {
    const message = error instanceof Error ? error.message : 'Could not publish this neighborhood.';
    setAdminMessage(message, true);
    setWorkflowText('Publishing stopped', 'Nothing else will run until this issue is corrected.');
    setWorkflowResult(message, true, false);
    showWorkflowFinishButton(true);
    workflowBusy = false;
    setPrimaryPublishBusy(false);
    setResetControlsDisabled(false);
  }

  function wirePublishWorkflow(): void {
    document.querySelector<HTMLButtonElement>('#close-publish-workflow')?.addEventListener('click', closePublishWorkflow);
    document.querySelector<HTMLButtonElement>('#finish-publish-workflow')?.addEventListener('click', closePublishWorkflow);
    document.querySelector<HTMLButtonElement>('#cancel-publish-reset')?.addEventListener('click', closePublishWorkflow);
    document.querySelector<HTMLDivElement>('.publish-workflow-backdrop')?.addEventListener('click', closePublishWorkflow);

    const input = document.querySelector<HTMLInputElement>('#publish-reset-input');
    const confirm = document.querySelector<HTMLButtonElement>('#confirm-publish-reset');
    input?.addEventListener('input', () => {
      if (confirm) confirm.disabled = input.value.trim().toUpperCase() !== 'RESET' || workflowBusy;
    });
    confirm?.addEventListener('click', () => void resetAndRebuild());
  }

  function openPublishWorkflow(): void {
    document.querySelector<HTMLDivElement>('#publish-workflow-modal')?.classList.remove('hidden');
    document.body.classList.add('admin-modal-open');
  }

  function closePublishWorkflow(): void {
    if (workflowBusy) return;
    document.querySelector<HTMLDivElement>('#publish-workflow-modal')?.classList.add('hidden');
    document.body.classList.remove('admin-modal-open');
    showResetConfirmation(false);
  }

  function resetPublishWorkflow(): void {
    setWorkflowStep('review', 'waiting');
    setWorkflowStep('reset', 'waiting');
    setWorkflowStep('build', 'waiting');
    setWorkflowResult('', false, false);
    showWorkflowFinishButton(false);
    showResetConfirmation(false);
    setResetControlsDisabled(false);
  }

  function setWorkflowStep(step: WorkflowStep, state: WorkflowStepState): void {
    const element = document.querySelector<HTMLLIElement>(`#publish-step-${step}`);
    if (!element) return;
    element.classList.remove('waiting', 'active', 'done', 'error', 'skipped', 'action');
    element.classList.add(state);
  }

  function setWorkflowText(title: string, description: string): void {
    const titleElement = document.querySelector<HTMLHeadingElement>('#publish-workflow-title');
    const descriptionElement = document.querySelector<HTMLParagraphElement>('#publish-workflow-description');
    if (titleElement) titleElement.textContent = title;
    if (descriptionElement) descriptionElement.textContent = description;
  }

  function setWorkflowResult(message: string, isError: boolean, allowHtml: boolean): void {
    const element = document.querySelector<HTMLDivElement>('#publish-workflow-result');
    if (!element) return;
    if (!message) {
      element.classList.add('hidden');
      element.textContent = '';
      return;
    }
    element.classList.remove('hidden', 'error', 'success');
    element.classList.add(isError ? 'error' : 'success');
    if (allowHtml) element.innerHTML = message;
    else element.textContent = message;
  }

  function showResetConfirmation(show: boolean): void {
    const panel = document.querySelector<HTMLDivElement>('#publish-reset-confirmation');
    const input = document.querySelector<HTMLInputElement>('#publish-reset-input');
    const confirm = document.querySelector<HTMLButtonElement>('#confirm-publish-reset');
    panel?.classList.toggle('hidden', !show);
    if (show) window.setTimeout(() => input?.focus(), 0);
    else {
      if (input) input.value = '';
      if (confirm) confirm.disabled = true;
    }
  }

  function setResetControlsDisabled(disabled: boolean): void {
    const input = document.querySelector<HTMLInputElement>('#publish-reset-input');
    const cancel = document.querySelector<HTMLButtonElement>('#cancel-publish-reset');
    const confirm = document.querySelector<HTMLButtonElement>('#confirm-publish-reset');
    if (input) input.disabled = disabled;
    if (cancel) cancel.disabled = disabled;
    if (confirm) {
      confirm.disabled = disabled || input?.value.trim().toUpperCase() !== 'RESET';
      confirm.textContent = disabled ? 'Resetting and rebuilding…' : 'Reset and rebuild automatically';
    }
  }

  function showWorkflowFinishButton(show: boolean): void {
    document.querySelector<HTMLButtonElement>('#finish-publish-workflow')?.classList.toggle('hidden', !show);
  }

  function setPrimaryPublishBusy(busy: boolean): void {
    const button = document.querySelector<HTMLButtonElement>('#publish-neighborhood');
    if (!button) return;
    button.disabled = busy || Boolean(review?.neighborhood.unmatchedIncludedHouses) || Boolean(review?.neighborhood.pendingReviewChanges);
    if (busy) button.textContent = 'Publishing…';
    else updatePrimaryPublishLabel();
  }

  function updatePrimaryPublishLabel(): void {
    const button = document.querySelector<HTMLButtonElement>('#publish-neighborhood');
    if (!button || !review) return;
    button.textContent = review.neighborhood.published ? 'Rebuild approved sections' : 'Finish review & publish';
  }

  function setAdminMessage(message: string, isError: boolean, allowHtml = false): void {
    const element = document.querySelector<HTMLDivElement>('#admin-message');
    if (!element) return;
    element.classList.remove('hidden', 'error', 'success');
    element.classList.add(isError ? 'error' : 'success');
    if (allowHtml) element.innerHTML = message;
    else element.textContent = message;
  }

  function showAdminError(error: unknown): void {
    console.error(error);
  }

  function wireLogout(): void {
    document.querySelectorAll<HTMLButtonElement>('[data-admin-sign-out], #admin-logout').forEach((button) => button.addEventListener('click', onSignOut));
  }
}

function normalizeAnalyticsName(value: string): string {
  return value.trim().toLocaleLowerCase().replace(/\s+/g, ' ');
}

function escapeAdminHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character] ?? character));
}
